import { useState, useEffect, useRef, useMemo } from 'react'
import { currentEncounterNumber } from '../../utils/caseEncounters'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { FileText, Clock, CheckCircle, Check, Share2, Calendar, RefreshCw, Stethoscope, Pill, AlertTriangle, ClipboardList, HelpCircle, Eye, Users, Upload, ChevronRight, ChevronDown, X, Plus, Lock, Search, Video, Home, Repeat } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { resolveActiveEncounter, urgencyToTiming } from '../../hooks/useActiveCarePlan'
import { useI18n } from '../../i18n'
import { isWithinCheckInWindow } from '../../components/casehome/appointmentWindow'
import { withdrawCaseFromDoctor, withdrawDestination } from '../../utils/withdrawCase'
import { canWithdrawCase } from '../../utils/canWithdraw'
import { offersNewShareCode } from '../../utils/shareCodeRow' // PROD-941
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { useAgent } from '../../components/AgentContext'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import { encountersAPI } from '../../services/api'
import ExternalDoctorLookup from '../../components/ExternalDoctorLookup'
import AppointmentEntry from '../../components/AppointmentEntry'
import VisitPrepDisclaimer from '../../components/VisitPrepDisclaimer'
import VisitPrepCodeDisplay from '../../components/VisitPrepCodeDisplay'
import ConfirmModal from '../../components/ConfirmModal'
import SwitchDoctorSheet from '../../components/share/SwitchDoctorSheet'
import CaseReportViewer from '../../components/share/CaseReportViewer' // s99 D476
import CheckInModal from '../../components/CheckInModal'
import { hasPendingReschedule, confirmedCheckInSlot } from '../../utils/pendingReschedule' // PROD-1007
import { physicianCancelOfRequest } from '../../utils/declinedRequest' // PROD-1137
import JoinVideoVisit from '../../components/JoinVideoVisit'
import { videoJoinTarget, isVirtualVisitType, joinCheckInPath } from '../../utils/videoVisit'
import QRCodeDisplay from '../../utils/qrcode'
import WalletPassModal from '../../components/WalletPassModal'
import { formatAddress } from '../../utils/formatAddress'
import { deliverNeedsOrellaMD as deliverNeedsOrellaMDFn } from '../../utils/caseGating'
import SubmitOverlay, { runOverlaySequence } from '../../components/SubmitOverlay'
import { formatDate, formatDateTime, formatCalendarDay, wallTimeToInstant } from '../../utils/dateUtils'
import AskOrellaChip from '../../components/AskOrellaChip'
import EligibilityBadge, { roleFromSpecialty } from '../../components/EligibilityBadge'
import { formatCaseSpecialty } from '../../utils/specialtyResolver'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import { deriveAuthRequired } from '../../utils/authRequired'
import ShareDoctorPanel from '../../components/share/ShareDoctorPanel'
import { matchDoctorsToCase, getMatchingSpecialties } from '../../utils/doctorMatch'
import { loadShareDoctorPanel } from '../../utils/shareDoctorPanel'
import { pollRefused } from '../../utils/pollRefused'
import { missingBeforeShare } from '../../utils/profileCompletion'
import ContinueWithoutInsurance from '../../components/share/ContinueWithoutInsurance' // PROD-1132 Q1
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { readAvsText } from '../../utils/avsText' // PROD-134 gamma: the one AVS reader
import { isEncounterNoteHeld } from '../../utils/noteHold' // PROD-1077: the one hold reader
import { OBSERVATION_ENABLED } from '../../config/featureFlags' // s102 D7
import { asText, asFirst } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import { isOutsideAppointment } from '../../utils/outsideAppointment' // PROD-1160
import { clinicianForms, clinicianFromText, clinicianName, withCaseClinician } from '../../utils/clinicianName.js'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// ===== Helper: Parse AVS content into structured sections =====
// v123.3: Safe text helper — ensures objects are never rendered as React children (error #300)
const safeText = (v) => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (typeof v === 'object') {
    // Common nested string fields
    if (v.avsContent) return String(v.avsContent)
    if (v.text) return String(v.text)
    if (v.content) return String(v.content)
    if (v.notes) return String(v.notes)
    if (v.summary) return String(v.summary)
    try { return JSON.stringify(v) } catch { return '[object]' }
  }
  return String(v)
}

const parseAVSIntoSections = (avsText, t) => {
  if (!avsText) return null
  
  const cleanText = avsText
    .replace(/[━═─┈┉┄┅╌╍│┃┆┇┊┋]/g, '')
    .replace(/^-{3,}\s*$/gm, '')           // Dash separator lines first
    .replace(/^[=*]{3,}\s*$/gm, '')        // Equals/asterisk separators
    .replace(/^[•]\s*\n\s*\n/gm, '')      // Standalone bullet followed by blank line (section divider)
    .replace(/^[•]\s*\n/gm, '• ')          // Join standalone bullet with next line
    .replace(/Questions\?\s*Try the Orella chat.*$/gm, '')  // Boilerplate
    .replace(/Thank you for choosing Orella.*$/gm, '')      // Boilerplate
    .replace(/\n{3,}/g, '\n\n')
  
  // Split on lines that are ALL-CAPS headers (≥3 chars, possibly with colons/numbers)
  // These are the AVS section boundaries
  const rawLines = cleanText.split('\n').map(l => l.trim()).filter(Boolean)
  
  // PRE-PASS: Join broken sentences where a line was split across a phrase
  // e.g. "...normal during\nExamination Findings\n, but you still..."  →  single line
  const lines = []
  for (let i = 0; i < rawLines.length; i++) {
    const prev = lines.length > 0 ? lines[lines.length - 1] : ''
    const curr = rawLines[i]
    const next = rawLines[i + 1] || ''
    
    // If current line starts with comma/semicolon/lowercase continuation → join to previous
    if (prev && /^[,;)}\].]/.test(curr)) {
      lines[lines.length - 1] = prev + curr  // No space before punctuation
      continue
    }
    // If previous line ends without terminal punctuation AND current is short mixed-case
    // AND next starts with comma/lowercase → this is a mid-sentence phrase, join all three
    if (prev && !/[.!?:•\-]$/.test(prev) && curr.length < 40 && curr !== curr.toUpperCase()
        && /^[,;a-z]/.test(next)) {
      lines[lines.length - 1] = prev + ' ' + curr
      continue
    }
    
    lines.push(curr)
  }
  
  // Detect header lines: either ALL-CAPS (≥3 word chars) OR known section title patterns
  const knownHeaders = [
    /^(your\s+)?diagnosis/i,
    /^(what we found|examination|exam)\s*(findings|on)?/i,
    /^(your\s+)?treatment\s*(plan)?/i,
    /^care\s+instructions/i,
    /^medications?(\s+prescribed)?/i,
    /^(warning\s+signs|when\s+to\s+seek)/i,
    /^follow[\s\-]?up(\s+plan|\s+recommended)?/i,
    /^(before\s+(your\s+)?next|next\s+steps)/i,
    /^reason\s+for\s+(your\s+)?visit/i,
    /^(after\s+visit\s+summary|patient\s+summary)/i,
    /^procedures?(\s+performed)?/i,
  ]
  const isHeader = (line) => {
    const stripped = line.replace(/[:\-–—\d.#•*]/g, '').trim()
    if (!stripped || stripped.length < 3) return false
    // All-caps check
    if (stripped === stripped.toUpperCase() && /[A-Z]{3,}/.test(stripped)) return true
    // Known section title check (must be a standalone short line, not mid-paragraph)
    // ★ AVS-SUMMARY-1: a "Label: value" line is content, never a title. "Diagnosis: Otitis
    //   media." is under 45 characters and matched /^diagnosis/, so a short diagnosis became an
    //   empty header and the diagnosis name was dropped from the screen.
    if (/:\s*\S/.test(line)) return false
    if (stripped.length < 45 && knownHeaders.some(re => re.test(stripped))) return true
    return false
  }
  
  // Group lines under headers
  const rawSections = []
  let currentHeader = null
  let currentLines = []
  
  for (const line of lines) {
    if (isHeader(line)) {
      // Save previous section
      if (currentHeader && currentLines.length > 0) {
        rawSections.push({ header: currentHeader, lines: [...currentLines] })
      } else if (!currentHeader && currentLines.length > 0) {
        // Content before first header → intro section
        rawSections.push({ header: '__INTRO__', lines: [...currentLines] })
      }
      currentHeader = line.replace(/[:\s]+$/, '').trim()
      currentLines = []
    } else {
      currentLines.push(line)
    }
  }
  // Don't forget the last section
  if (currentHeader && currentLines.length > 0) {
    rawSections.push({ header: currentHeader, lines: currentLines })
  }
  
  if (rawSections.length === 0) return null
  
  // Map raw header text → display config
  const headerMap = [
    { match: /REASON\s+FOR|YOUR\s+VISIT|CHIEF\s+COMPLAINT/i, title: t('diagnose.reasonForYourVisit'), icon: 'clipboard', color: 'navy' },
    { match: /DIAGNOSIS|YOUR DIAGNOSIS/i, title: t('diagnose.yourDiagnosis'), icon: 'stethoscope', color: 'navy' },
    { match: /WHAT WE FOUND|EXAMINATION|EXAM FINDINGS/i, title: t('diagnose.examinationFindings'), icon: 'clipboard', color: 'gray' },
    { match: /PROCEDURE/i, title: t('diagnose.procedures'), icon: 'clipboard', color: 'gray' },
    { match: /TREATMENT PLAN|CARE INSTRUCTION|WHAT TO DO|AT HOME/i, title: t('diagnose.careInstructions'), icon: 'check', color: 'green' },
    { match: /MEDICATION/i, title: t('diagnose.medications'), icon: 'pill', color: 'blue' },
    { match: /WARNING|WHEN TO SEEK|SEEK CARE/i, title: t('diagnose.whenToSeekCare'), icon: 'alert', color: 'red' },
    { match: /FOLLOW.?UP|YOUR NEXT|NEXT APPOINTMENT|NEXT STEPS/i, title: t('diagnose.followupPlan'), icon: 'calendar', color: 'amber' },
    { match: /BEFORE YOUR|BEFORE NEXT|BEFORE APPOINTMENT/i, title: t('diagnose.beforeNextAppointment'), icon: 'clipboard', color: 'amber' },
  ]
  
  // Headers to skip entirely (page-level titles, not content sections)
  const skipHeaders = /AFTER\s+VISIT\s+SUMMARY|PATIENT\s+SUMMARY|^PATIENT:/i
  
  const sections = []
  const usedTitles = new Set()
  
  for (const raw of rawSections) {
    if (raw.header === '__INTRO__') continue
    // Skip page-level title headers (After Visit Summary, etc.)
    if (skipHeaders.test(raw.header)) continue // Skip pre-header content
    
    // Find matching config
    let config = null
    for (const h of headerMap) {
      if (h.match.test(raw.header)) { config = h; break }
    }
    if (!config) {
      // Unknown header — treat as generic section
      config = { 
        title: raw.header.split(/\s+/).map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' '), 
        icon: 'clipboard', color: 'gray' 
      }
    }
    
    // Clean content: remove lines that are just repeating the section title
    const content = raw.lines
      .filter(line => {
        const stripped = line.replace(/[:\-–—•*#]/g, '').trim()
        // Skip if line is just the section title repeated
        if (stripped.toUpperCase() === raw.header.toUpperCase()) return false
        if (stripped.toUpperCase() === config.title.toUpperCase()) return false
        // Skip standalone all-caps sub-headers that match our own title
        // BUT keep sub-labels ending with ":" (e.g. "Medications Prescribed:")
        if (isHeader(line) && !line.trim().endsWith(':') && headerMap.some(h => h.match.test(stripped))) return false
        // Skip empty decorative lines
        if (/^[-=]+$/.test(stripped)) return false
        return true
      })
    
    if (content.length === 0) continue
    
    // Merge "Procedures" into "Examination Findings" if both exist
    if (config.title === 'Procedures' && usedTitles.has(t('diagnose.examinationFindings'))) {
      const examSection = sections.find(s => s.title === t('diagnose.examinationFindings'))
      if (examSection) {
        examSection.content.push('') // spacer
        examSection.content.push(t('diagnose.proceduresPerformed'))
        examSection.content.push(...content)
        continue
      }
    }
    
    // Merge duplicate section titles (e.g. two "Follow-Up" sections)
    if (usedTitles.has(config.title)) {
      const existing = sections.find(s => s.title === config.title)
      if (existing) {
        existing.content.push(...content)
        continue
      }
    }
    
    usedTitles.add(config.title)
    sections.push({ title: config.title, icon: config.icon, color: config.color, content })
  }
  
  // POST-PROCESSING: Extract "Procedures Performed..." from Medications → Examination Findings
  const medsSection = sections.find(s => s.title === t('diagnose.medications'))
  const examSection = sections.find(s => s.title === t('diagnose.examinationFindings'))
  if (medsSection) {
    const procIdx = medsSection.content.findIndex(l => /^Procedures?\s+(Performed|Done|Completed)/i.test(l))
    if (procIdx !== -1) {
      const procContent = medsSection.content.splice(procIdx)
      if (examSection) {
        examSection.content.push('', ...procContent)
      } else {
        // Create Exam section with just procedures
        const insertIdx = sections.indexOf(medsSection)
        sections.splice(insertIdx, 0, { 
          title: t('diagnose.examinationFindings'), icon: 'clipboard', color: 'gray', content: procContent 
        })
      }
    }
    // Also extract "What To Do At Home" from Medications → Care Instructions
    const homeIdx = medsSection.content.findIndex(l => /^What To Do At Home/i.test(l))
    if (homeIdx !== -1) {
      const homeContent = medsSection.content.splice(homeIdx)
      const careSection = sections.find(s => s.title === t('diagnose.careInstructions'))
      if (careSection) {
        careSection.content.push('', ...homeContent)
      } else {
        // Create Care Instructions after Medications
        const insertIdx = sections.indexOf(medsSection) + 1
        sections.splice(insertIdx, 0, { 
          title: t('diagnose.careInstructions'), icon: 'check', color: 'green', content: homeContent 
        })
      }
    }
  }
  
  // ★★★ AVS-SUMMARY-1 (2026-10-12) — THE SUMMARY LINE IS THE AVS'S OWN TEXT. The generator
  //   writes every section as "In short: <one sentence>" followed by the details, so what the
  //   patient sees first is the AVS the MD finalized, never a second write-up. The first such
  //   line becomes `summary`; any later one (a section merged in, e.g. Procedures into
  //   Examination) stays in the details with its prefix removed, so nothing is dropped.
  //   A section with no summary line (an AVS written before this change) has summary null
  //   and opens in full, as before.
  for (const sec of sections) {
    let summary = null
    sec.content = sec.content.reduce((out, line) => {
      const txt = safeText(line)
      const m = txt.match(AVS_SUMMARY_RE)
      if (!m) { out.push(line); return out }
      const body = txt.slice(m[0].length).trim()
      if (!body) return out
      if (summary === null) summary = body
      else out.push(body)
      return out
    }, [])
    sec.summary = summary
  }

  return sections.length > 0 ? sections : null
}

// The prefix the AVS generator writes on each section's summary line (api/ai_service.py
// AVS_GENERATION_PROMPT). Spanish kept for an AVS rendered in es.
const AVS_SUMMARY_RE = /^\s*[•\-]?\s*(in short|en resumen)\s*:\s*/i

// ===== Shared AVS presentation primitives =====
// ONE colour map, ONE icon map, ONE line renderer.
//
// ★ BEFORE THIS THERE WERE THREE COPIES OF EACH: AVSSection had one, AVSWalkthrough
//   re-declared colorStyles and IconMap and then inlined a byte-identical copy of
//   the line renderer, and the checklist below would have been the fourth. RULES
//   1bv(a) — a correction applied where it was reported is not a correction. The
//   only way a fix reaches every reader is for there to be one reader.
// ★★★ TEST 1.15 — THE STATUS POLL'S BUDGET, at module scope so it can be read without
//   opening a 3,400-line component. MEASURED 2026-09-21 on the shipped build: the poll ran
//   on a flat 15,000 ms setInterval, forever, with no visibility check — and each tick runs
//   a FULL loadCases(): one GET of the entire case list (never the one case this screen is
//   waiting on, although casesAPI.get(caseId) exists), a JSON.parse of every case's
//   after_visit_summary, the whole status/encounter merge, and the store's persist write.
//   loadCases' per-case section alone emits 26 console.log statements per cycle.
//   A screen left open 30 minutes cost 120 of those cycles, on a phone, on cellular.
// ★★ WHAT IT IS WAITING FOR IS A DOCTOR CLAIMING A CASE — minutes to hours, not seconds.
//   So: 15 s to start (unchanged, because the first minute IS when a claim is plausible
//   after a fresh share), then geometric backoff to a five-minute ceiling, and NOTHING AT
//   ALL while the tab is hidden. 30 minutes visible is 11 cycles instead of 120; 30 minutes
//   in the background is zero instead of 120. Becoming visible again resets to 15 s and
//   polls at once, so a returning patient sees fresher data than the old code gave them.
// ★ THE SHAPE IS THE ONE THIS APP ALREADY USES: useNotificationStore.startPolling guards on
//   document.visibilityState === 'visible' and debounces rapid tab switches by 5 s. Same
//   idiom here rather than a second one.
const POLL_FIRST_MS = 15000
const POLL_CEILING_MS = 300000
const POLL_BACKOFF = 1.5
const POLL_DEBOUNCE_MS = 5000

const AVS_COLOR_STYLES = {
  navy:  { border: 'border-l-[#15354F]', bg: 'bg-[#15354F]/5', titleColor: 'text-[#15354F]', iconColor: 'text-[#15354F]' },
  gray:  { border: 'border-l-[#94A3B8]', bg: 'bg-gray-50',      titleColor: 'text-gray-600',  iconColor: 'text-gray-400' },
  green: { border: 'border-l-[#16A34A]', bg: 'bg-green-50',     titleColor: 'text-green-700', iconColor: 'text-green-600' },
  blue:  { border: 'border-l-[#46A8F9]', bg: 'bg-[#E9F8FF]',    titleColor: 'text-[#46A8F9]', iconColor: 'text-[#46A8F9]' },
  red:   { border: 'border-l-[#EF4444]', bg: 'bg-red-50',       titleColor: 'text-red-600',   iconColor: 'text-red-500' },
  amber: { border: 'border-l-[#F59E0B]', bg: 'bg-amber-50',     titleColor: 'text-amber-700', iconColor: 'text-amber-600' },
}
const AVS_ICONS = {
  stethoscope: Stethoscope, pill: Pill, alert: AlertTriangle,
  calendar: Calendar, clipboard: ClipboardList, check: CheckCircle,
  question: HelpCircle,
}
const avsStyle = (color) => AVS_COLOR_STYLES[color] || AVS_COLOR_STYLES.gray
const avsIcon = (icon) => AVS_ICONS[icon] || FileText

// The ONE renderer for AVS body lines. Both AVSSection and AVSChecklist call it.
const AVSLines = ({ content, iconColor }) => (
  <>
    {(content || []).map((line, idx) => {
      const safeLine = safeText(line)
      if (!safeLine || !safeLine.trim()) return <div key={idx} className="h-2" />

      if (safeLine.startsWith('•') || safeLine.startsWith('-')) {
        const text = safeLine.replace(/^[•\-]\s*/, '')
        return (
          <div key={idx} className="flex gap-2 text-[14px] leading-relaxed text-gray-600">
            <span className={`${iconColor} flex-shrink-0`}>•</span>
            <span>{text}</span>
          </div>
        )
      }
      if (/^[A-Z][^•]*:$/.test(safeLine) && safeLine.length < 60) {
        return <p key={idx} className="text-[13px] font-bold text-gray-700 mt-2 first:mt-0">{safeLine}</p>
      }
      const colonMatch = safeLine.match(/^([A-Z][^:]{2,40}):\s+(.+)/)
      if (colonMatch) {
        return (
          <p key={idx} className="text-[14px] leading-relaxed text-gray-600">
            <strong className="text-gray-800">{colonMatch[1]}:</strong> {colonMatch[2]}
          </p>
        )
      }
      return <p key={idx} className="text-[14px] leading-relaxed text-gray-600">{safeLine}</p>
    })}
  </>
)

// ===== One section's body: the AVS's summary line, then its details =====
// ★ AVS-SUMMARY-1: the details sit behind Learn more. WHEN TO SEEK CARE (color red) is the
//   exception and always opens in full: a warning sign must never be one tap away.
const AVSBody = ({ summary, content, color, iconColor }) => {
  const { t } = useI18n()
  const [more, setMore] = useState(false)
  const hasDetails = (content || []).some((l) => safeText(l).trim())
  if (!summary) return <AVSLines content={content} iconColor={iconColor} />
  const alwaysFull = color === 'red'
  return (
    <>
      <p data-avs-summary className="text-[14.5px] leading-relaxed font-medium text-[#15354F]">{summary}</p>
      {hasDetails && (alwaysFull || more) ? (
        <div className={alwaysFull ? 'space-y-1 pt-1' : 'space-y-1 pt-2 mt-1 border-t border-dashed border-gray-200'}>
          <AVSLines content={content} iconColor={iconColor} />
        </div>
      ) : null}
      {hasDetails && !alwaysFull ? (
        <button
          type="button"
          data-avs-learn-more
          aria-expanded={more}
          onClick={() => setMore((v) => !v)}
          className="text-[12.5px] font-semibold text-[#46A8F9] pt-1"
        >
          {more ? (t('diagnose.avsShowLess') || 'Show less') : (t('diagnose.avsLearnMore') || 'Learn more')}
        </button>
      ) : null}
    </>
  )
}

// ===== AVS Section Card Component =====
const AVSSection = ({ title, icon, color, content, summary }) => {
  const s = avsStyle(color)
  const IconComponent = avsIcon(icon)

  return (
    <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 overflow-hidden">
      <div className={`flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] ${s.border} ${s.bg}`}>
        <IconComponent size={15} className={s.iconColor} />
        <h4 className={`text-[13px] font-bold uppercase tracking-[0.5px] ${s.titleColor}`}>{title}</h4>
      </div>
      <div className="px-3.5 py-3 space-y-1">
        <AVSBody summary={summary} content={content} color={color} iconColor={s.iconColor} />
      </div>
    </div>
  )
}

// ===== AVS review: reading level + checklist =====
//
// REPLACES AVSWalkthrough (v212.0 - v217.1.4). That component revealed one section
// at a time and left every completed one FULLY EXPANDED, so the page only ever grew
// and the Continue control travelled down it; "1 of 8" appeared only on the current
// card, so the patient never saw what the eight sections were; and the green CTA sat
// disabled for the whole walkthrough with no stated reason (D164 — a status is not a
// disabled action). Reported on TestFlight build 42: "a more user friendly way to
// review each 8 steps" and "explanation literacy/length options".
//
// Mockup: claude/orella_diagnose_review_mockup_v1.html, option B, chosen 2026-08-22.

// The level, its storage and its three ids now live in utils/readingLevel.js —
// the pre-visit summary is the second screen to want them, and two copies of a
// storage key is how a patient's choice stops following them between screens.

// The collapsed row's one line. It is the section's OWN opening sentence, verbatim
// and clamped — never a generated summary. A second AI-written sentence sitting
// under a green tick would be a claim about what the patient read.
const avsTakeaway = (content) => {
  const first = (content || []).map(safeText).find((l) => {
    const s = (l || '').trim()
    if (!s) return false
    if (/^[A-Z][^•]*:$/.test(s) && s.length < 60) return false
    return true
  })
  if (!first) return ''
  const body = first.replace(/^[•\-]\s*/, '').trim()
  const m = body.match(/^([\s\S]{20,150}?[.!?])(\s|$)/)
  const out = m ? m[1] : body
  return out.length > 130 ? `${out.slice(0, 127)}…` : out
}

const AVSChecklistKeyframes = () => (
  <style>{`
    @keyframes avsRowOpen {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `}</style>
)

const AVSProgressRing = ({ done, total }) => {
  const R = 15.5
  const CIRC = 2 * Math.PI * R
  const pct = total > 0 ? Math.min(done / total, 1) : 0
  return (
    <svg width="40" height="40" viewBox="0 0 36 36" className="flex-shrink-0">
      <circle cx="18" cy="18" r={R} fill="none" stroke="#E2E8F0" strokeWidth="4" />
      <circle
        cx="18" cy="18" r={R} fill="none" stroke="#16A34A" strokeWidth="4" strokeLinecap="round"
        strokeDasharray={CIRC} strokeDashoffset={CIRC - CIRC * pct}
        transform="rotate(-90 18 18)"
        style={{ transition: 'stroke-dashoffset 0.35s ease' }}
      />
    </svg>
  )
}

const AVSChecklist = ({ sections, gated, onAllOpened, t }) => {
  const total = sections ? sections.length : 0
  // ★ AVS-SUMMARY-1 / x_15: ungated (already confirmed) starts with every section closed.
  const [openIdx, setOpenIdx] = useState(gated ? 0 : -1)
  const [opened, setOpened] = useState(() => new Set(total > 0 ? [0] : []))
  const notifiedRef = useRef(false)

  // Fires once, when every section has been opened at least once. Guarded by a ref
  // rather than by the parent's state, because onAllOpened is a fresh arrow on every
  // render and would otherwise re-fire on each one.
  useEffect(() => {
    if (!gated || total === 0 || notifiedRef.current) return
    if (opened.size >= total) {
      notifiedRef.current = true
      onAllOpened?.()
    }
  }, [opened, total, gated, onAllOpened])

  if (!sections || total === 0) return null

  // ★★ x_15 (TestFlight b65, Alex): "looks a lot off if I open it after accepting it". The
  //   confirmed view used to render every section fully open, the whole AVS at once. Now it is
  //   the same accordion with no gate, no ticks and no progress: closed sections show their
  //   summary line and the patient opens any of them in any order.

  const toggle = (idx) => {
    setOpenIdx((prev) => (prev === idx ? -1 : idx))
    setOpened((prev) => (prev.has(idx) ? prev : new Set(prev).add(idx)))
  }

  const doneCount = opened.size

  return (
    <>
      <AVSChecklistKeyframes />

      {gated ? (
      <div className="flex items-center gap-3 px-0.5 pt-1">
        <AVSProgressRing done={doneCount} total={total} />
        <div className="min-w-0">
          <p className="text-[12.5px] font-bold text-[#15354F]">
            {t('diagnose.avsReviewedCount', { done: doneCount, total })}
          </p>
          <p className="text-[11px] text-gray-400 mt-[1px]">
            {doneCount >= total ? t('diagnose.avsCanConfirm') : t('diagnose.avsOpenEachPart')}
          </p>
        </div>
      </div>
      ) : null}

      {sections.map((section, idx) => {
        const s = avsStyle(section.color)
        const SectionIcon = avsIcon(section.icon)
        const isOpen = idx === openIdx
        const isDone = opened.has(idx)
        const takeaway = !isOpen && (isDone || !gated) ? (section.summary || avsTakeaway(section.content)) : ''

        return (
          <div key={idx} className="bg-white rounded-[14px] border-[1.5px] border-gray-200 overflow-hidden">
            <button
              onClick={() => toggle(idx)}
              aria-expanded={isOpen}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left border-l-[3px] ${
                isOpen ? `${s.border} ${s.bg}` : (gated && isDone) ? 'border-l-[#16A34A] bg-[#F8FDF9]' : 'border-l-transparent bg-white'
              }`}
            >
              {gated ? (
              <span
                className={`w-[19px] h-[19px] rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDone ? 'bg-[#16A34A]' : 'border-[1.7px] border-gray-300'
                }`}
              >
                {isDone ? <Check size={11} className="text-white" strokeWidth={3} /> : null}
              </span>
              ) : null}
              {(isOpen || !gated) ? <SectionIcon size={15} className={s.iconColor} /> : null}
              <h4 className={`flex-1 text-[12.5px] font-bold uppercase tracking-[0.5px] ${isOpen ? s.titleColor : 'text-gray-500'}`}>
                {section.title}
              </h4>
              {gated ? (
              <span className="text-[10.5px] font-medium text-gray-300 flex-shrink-0">
                {idx + 1}/{total}
              </span>
              ) : null}
              <ChevronDown
                size={15}
                className={`text-gray-300 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isOpen ? (
              <div className="px-3.5 py-3 space-y-1" style={{ animation: 'avsRowOpen 0.22s ease' }}>
                <AVSBody key={idx} summary={section.summary} content={section.content} color={section.color} iconColor={s.iconColor} />
                {gated && idx < total - 1 ? (
                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      data-avs-next
                      onClick={() => toggle(idx + 1)}
                      className="text-[12.5px] font-semibold text-white bg-[#15354F] rounded-full px-3.5 py-1.5"
                    >
                      {t('diagnose.avsNextSection') || 'Next section'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {takeaway ? (
              <p className="px-3.5 pb-2.5 pl-[42px] text-[12px] text-gray-400 leading-relaxed">{takeaway}</p>
            ) : null}
          </div>
        )
      })}
    </>
  )
}

// ★ AVS-SUMMARY-1 (2026-10-12, Dr. Rami): the Simpler / Standard / Detailed control is
//   removed. Each section now opens with the AVS's own summary line and keeps the details
//   behind Learn more, so the AVS is the one text. /ai/avs-reading-level stays on the API,
//   unreferenced, until the session cleanup retires it.
const AVSReview = ({ avsText, gated, onAllOpened, t }) => {
  const sections = useMemo(() => parseAVSIntoSections(avsText, t), [avsText, t])
  if (!sections || sections.length === 0) return null
  return <AVSChecklist sections={sections} gated={gated} onAllOpened={onAllOpened} t={t} />
}

// ===== Follow-Up Update Card (purple) =====
const FollowUpUpdateCard = ({ data, caseData }) => {
  const { t } = useI18n()
  const followUpDiagnose = caseData.followUpDiagnose || {}
  const diagnoseData = caseData.diagnose || {}
  const status = followUpDiagnose.status || diagnoseData.followUpStatus
  const summary = followUpDiagnose.summary || diagnoseData.followUpSummary
  const treatment = followUpDiagnose.treatment || diagnoseData.followUpTreatment
  const nextSteps = followUpDiagnose.nextSteps || diagnoseData.followUpNextSteps
  const note = diagnoseData.followUpNote
  
  const statusStyles = {
    resolved:  { pill: 'bg-green-100 text-green-700', label: t('diagnose.statusResolved') },
    improving: { pill: 'bg-blue-100 text-blue-700', label: t('diagnose.statusImproving') },
  }
  const statusInfo = statusStyles[status] || { pill: 'bg-amber-100 text-amber-700', label: t('diagnose.statusOngoing') }
  
  return (
    <div className="bg-white rounded-[14px] border-[1.5px] border-purple-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-purple-500 bg-purple-50">
        <RefreshCw size={15} className="text-purple-600" />
        <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-purple-700">{t('diagnose.followUpUpdate')}</h4>
      </div>
      <div className="px-3.5 py-3 space-y-2">
        {status && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg ${statusInfo.pill}`}>
            {statusInfo.label}
          </span>
        )}
        {summary && <p className="text-[12.5px] text-gray-600 leading-relaxed">{safeText(summary)}</p>}
        {treatment && <p className="text-[12.5px] text-gray-600 leading-relaxed"><strong className="text-purple-700">{t('diagnose.updatedTreatment')}</strong> {safeText(treatment)}</p>}
        {nextSteps && <p className="text-[12.5px] text-gray-600 leading-relaxed"><strong className="text-purple-700">{t('diagnose.nextSteps')}</strong> {safeText(nextSteps)}</p>}
        {note && !summary && <p className="text-[12.5px] text-gray-600 leading-relaxed">{safeText(note)}</p>}
      </div>
    </div>
  )
}

// ===== Follow-Up Updates List (multiple) =====
const FollowUpUpdatesList = ({ updates }) => {
  const { t } = useI18n()
  if (!updates || updates.length === 0) return null
  return (
    <>
      {updates.map((update, idx) => (
        <div key={idx} className="bg-white rounded-[14px] border-[1.5px] border-purple-200 overflow-hidden">
          <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-purple-500 bg-purple-50">
            <RefreshCw size={15} className="text-purple-600" />
            <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-purple-700">{t('diagnose.followUpNumPrefix')}{idx + 1}</h4>
            {update.date && <span className="ml-auto text-[10px] text-purple-400">{formatDate(update.date)}</span>}
          </div>
          <div className="px-3.5 py-3 space-y-1">
            {update.summary && <p className="text-[12.5px] text-gray-600 leading-relaxed">{safeText(update.summary)}</p>}
            {update.nextSteps && (
              <p className="text-[12.5px] text-gray-500"><strong>{t('diagnose.nextSteps')}</strong> {safeText(update.nextSteps)}</p>
            )}
          </div>
        </div>
      ))}
    </>
  )
}

// ===== Follow-Up Type Cards =====
const FollowUpTypeCard = ({ type, data, referralData }) => {
  const { t } = useI18n()
  if (type === 'in_follow_up' || type === 'followup') {
    return (
      <div className="bg-white rounded-[14px] border-[1.5px] border-blue-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-blue-500 bg-blue-50">
          <Calendar size={15} className="text-blue-500" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-blue-700">{t('diagnose.followUpRecommended')}</h4>
          <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded bg-blue-500 text-white">{t('diagnose.inFollowUp')}</span>
        </div>
        <div className="px-3.5 py-3">
          <p className="text-[12.5px] text-gray-600">
            {t('diagnose.fuRecommendedVisit')}{asText(data.timeframe) ? t('diagnose.fuWithin', { timeframe: asText(data.timeframe) }) : ''}.
          </p>
        </div>
      </div>
    )
  }
  
  if (type === 'observation') {
    return (
      <div className="bg-white rounded-[14px] border-[1.5px] border-amber-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-amber-500 bg-amber-50">
          <Eye size={15} className="text-amber-500" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-amber-700">{t('diagnose.observationPeriod')}</h4>
          <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded bg-amber-500 text-white">{t('diagnose.observation')}</span>
        </div>
        <div className="px-3.5 py-3">
          <p className="text-[12.5px] text-gray-600">
            {t('diagnose.observationBody')}
          </p>
          {data.observationNotes && asText(data.observationNotes) && (
            <div className="bg-amber-50 rounded-lg p-2.5 mt-2 text-[12px] text-amber-800">
              <strong>{t('diagnose.whatToWatch')}</strong> {asText(data.observationNotes)}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  if (type === 'referral' || type === 'refer') {
    return (
      <div className="bg-white rounded-[14px] border-[1.5px] border-purple-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-purple-500 bg-purple-50">
          <Users size={15} className="text-purple-500" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-purple-700">{t('diagnose.specialistReferral')}</h4>
          <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded bg-purple-500 text-white">{t('diagnose.referral')}</span>
        </div>
        <div className="px-3.5 py-3">
          <p className="text-[12.5px] text-gray-700 font-semibold">{t('diagnose.referredToLabel')} {asText(referralData?.specialty) || t('diagnose.specialist')}</p>
          {asText(referralData?.doctorName) && <p className="text-[12px] text-gray-500 mt-1">{t('diagnose.providerLabel')} {asText(referralData.doctorName)}</p>}
          {asFirst(referralData?.notes, data.referralNotes) && <p className="text-[12px] text-gray-500 mt-1">{asFirst(referralData?.notes, data.referralNotes)}</p>}
        </div>
      </div>
    )
  }
  
  if (type === 'completed' || type === 'complete' || type === 'pcp') {
    return (
      <div className="bg-white rounded-[14px] border-[1.5px] border-green-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-green-500 bg-green-50">
          <CheckCircle size={15} className="text-green-500" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-green-700">{t('diagnose.careComplete')}</h4>
          <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded bg-green-500 text-white">{t('diagnose.resolved')}</span>
        </div>
        <div className="px-3.5 py-3">
          <p className="text-[12.5px] text-gray-600">
            {t('diagnose.careCompleteBody')}
          </p>
        </div>
      </div>
    )
  }
  
  return null
}

// ===== Utility: Format specialty name =====
const formatSpecialtyName = (name) => {
  if (!name) return ''
  return name.replace(/\(([a-z])/gi, (m, p1) => `(${p1.toUpperCase()}`).replace(/(^|\s)([a-z])/g, (m, s, c) => s + c.toUpperCase())
}

// ========================================================
// ===== MAIN COMPONENT =====
// ========================================================
// v225.24.0: Shared pending-consult view — doctor card + confirmed-appointment
// card + (no-data) awaiting fallback. Single source of truth so the INITIAL and
// FOLLOW-UP Diagnose "pending" states render identically. Each call site
// normalizes its own appointment shape (initial: appointmentRequest JSONB;
// follow-up: appointment row) into these props before passing them in.
// v242.79.0 (TF-36) / MEET PA2: the case's INITIAL appointment row (the link lives on the row, not
// on appointmentRequest), else its first appointment. Shared by the pending card's Join and the
// check-in modal, so both join the same visit.
function initialAppointmentRow(caseObj) {
  const list = (caseObj?.appointments || []).filter((x) => !isOutsideAppointment(x))
  return list.find((x) => x.appointment_category === 'initial') || list[0] || null
}

function PendingConsultCard({
  docName, docSpecialty, docPracticeName, docPhoto, docInitials,
  statusLabel, statusTone = 'blue',
  apptTitle, apptDateISO, apptTime, apptType, isApptConfirmed, joinTarget, onJoinCheckIn = null,
  onReschedule, fallbackTitle, fallbackBody, t,
  eligibility, onViewProfile,
}) {
  const hasDoc = !!docName
  const hasAppt = !!apptDateISO
  let apptDateLabel = ''
  if (apptDateISO) {
    try {
      apptDateLabel = formatCalendarDay(apptDateISO, { weekday: 'long', month: 'long', day: 'numeric' }, 'en-US') || String(apptDateISO)
    } catch { apptDateLabel = String(apptDateISO) }
  }
  const isVirtual = apptType === 'video' || apptType === 'telehealth' || apptType === 'virtual'
  // v242.101.0 — the checked-in tone was teal. Both tones now sit on the ice
  // tint; checked-in reads stronger through a navy label and a full-blue border,
  // so the two stay distinguishable without a hue outside the brand palette.
  const statusPillClass = statusTone === 'checkin'
    ? 'bg-[#E9F8FF] text-[#15354F] border border-[#46A8F9]'
    : 'bg-[#E9F8FF] text-[#46A8F9] border border-[#A7D9FC]'
  return (
    <>
      {/* Doctor card */}
      {hasDoc && (
        <div className="bg-white border-[1.5px] border-[#A7D9FC] rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white border-2 border-[#A7D9FC] rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              {docPhoto ? (
                <img src={docPhoto} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[#46A8F9] font-bold text-sm">{docInitials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#15354F] text-[14px]">{docName}</p>
              {docSpecialty && <p className="text-[#46A8F9] text-[12px] font-medium">{formatProviderSpecialty(docSpecialty, t)}</p>}
              {docPracticeName && <p className="text-[11px] text-gray-400 mt-0.5">{docPracticeName}</p>}
            </div>
            {statusLabel && (
              <div className={`px-2.5 py-1 rounded-lg text-[10px] font-bold self-start ${statusPillClass}`}>{statusLabel}</div>
            )}
          </div>

          {/* v230.2.3 — Basic eligibility under the specialist name. Renders
              nothing when no last_eligibility exists (self-pay / pre-270 cases). */}
          {eligibility && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <EligibilityBadge
                eligibility={eligibility}
                variant="compact"
                doctorRole={roleFromSpecialty(docSpecialty)}
              />
            </div>
          )}

          {/* v235.1.0 — auth-required pill mirrors the booking surface */}
          {deriveAuthRequired(null, eligibility) && (
            <div className="mt-2 flex items-center gap-2 rounded-full px-3 py-2" style={{ border: '1.5px solid #FCD9A3', color: '#B45309', background: '#fff', width: 'fit-content' }}>
              <AlertTriangle size={14} style={{ color: '#B45309' }} />
              <span className="text-[12px] font-bold">{t('shareConv.authRequiredPill', 'Authorization required')}</span>
            </div>
          )}

          {/* v230.2.3 — Link to the doctor's full Orella profile. */}
          {onViewProfile && (
            <button
              onClick={onViewProfile}
              className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold active:opacity-70"
              style={{ color: '#46A8F9' }}
            >
              {t('diagnose.viewProfile', 'View profile')}
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      )}

      {/* Appointment info */}
      {hasAppt && (
        <div className={`rounded-2xl p-4 border-[1.5px] ${isApptConfirmed ? 'bg-white border-[#A7D9FC]' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={14} className="text-[#46A8F9]" />
            <span className="font-bold text-[#15354F] text-[12px]">{apptTitle}</span>
            {isApptConfirmed && (
              <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: '#E9F8FF', color: '#46A8F9' }}>{t('diagnose.confirmedShort')}</span>
            )}
          </div>
          <p className="text-[13px] text-[#15354F] font-medium">{apptDateLabel} at {apptTime}</p>
          <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
            <Clock size={10} />
            {isVirtual ? t('diagnose.virtualVisit') : t('diagnose.inPersonVisit')}
            {isApptConfirmed && t('diagnose.preparingForVisit')}
            {!isApptConfirmed && t('diagnose.pendingConfirmation')}
          </p>
          {/* MEET PA2: joinTarget = videoJoinTarget(...) of the visit; the link opens only through
              JoinVideoVisit (the recording question first). */}
          {joinTarget && isApptConfirmed && (
            <JoinVideoVisit
              appointment={joinTarget}
              beforeOpen={onJoinCheckIn}
              className="inline-flex items-center gap-1.5 mt-2.5 text-[11px] font-semibold text-[#46A8F9] bg-[#E9F8FF] px-3 py-1.5 rounded-lg border border-[#A7D9FC] hover:bg-[#D4EFFF] transition-colors"
              icon={<Video size={12} />}
            />
          )}
          {onReschedule && (
            <button
              onClick={onReschedule}
              className="flex items-center gap-1.5 mt-2.5 text-[11px] font-semibold transition-colors active:opacity-70"
              style={{ color: '#46A8F9' }}
            >
              <RefreshCw size={12} />
              {t('diagnose.rescheduleAppointment')}
            </button>
          )}
        </div>
      )}

      {/* Status card (when no appointment and no doctor details) */}
      {!hasDoc && !hasAppt && (
        <div className="bg-gray-50 border-[1.5px] border-gray-200 rounded-2xl p-7 text-center">
          <div className="w-14 h-14 bg-[#E9F8FF] rounded-2xl flex items-center justify-center mx-auto mb-4 relative">
            <div className="absolute inset-[-6px] rounded-[20px] border-2 border-[#46A8F9]/20 animate-[ringPulse_2s_ease-in-out_infinite]" />
            <Clock size={28} className="text-[#46A8F9]" strokeWidth={1.5} />
          </div>
          <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{fallbackTitle || t('diagnose.awaitingDoctorReview')}</h3>
          <p className="text-[12px] text-gray-400 leading-relaxed mb-4">{fallbackBody || 'Your doctor is reviewing your case details, uploads, and symptom history.'}</p>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════
// FOLLOW-UP DIAGNOSE VIEW (v120)
// Renders when currentEncounter >= 2 and encounterPhase is a diagnose phase
// Shows new AVS from encounter data + collapsible "Previous Visit"
// ═══════════════════════════════════════════════════════════════
function FollowUpDiagnoseView({ currentCase, encounterNum, encounterPhase, navigate, updateAndSaveCase, loadCases, t }) {
  // ★★★ v242.185.0 — THIS COMPONENT CALLED `openAgent?.()` AND NOTHING BOUND IT. The name is
  //   destructured from useAgent() 43 lines further down, in a DIFFERENT component, so the
  //   optional call here threw ReferenceError rather than doing nothing — `?.()` guards a
  //   value that is undefined, never a name that is unbound.
  const { openAgent } = useAgent()
  const [showContent, setShowContent] = useState(false)
  const [showPrevious, setShowPrevious] = useState(false)
  const [isAcknowledging, setIsAcknowledging] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [submitPhase, setSubmitPhase] = useState(null)
  // v225.24.0: "Check for updates" refresh in the follow-up pending state (parity
  // with the initial pending view).
  const [isRefreshing, setIsRefreshing] = useState(false)
  const handleFollowUpRefresh = async () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    try { await loadCases?.() } catch (e) { console.warn('[FollowUpDiagnose] refresh failed:', e) }
    finally { setTimeout(() => setIsRefreshing(false), 600) }
  }
  // v217.1.4: Gate "I've Reviewed This" on walkthrough completion (same
  // pattern as the main AVS surface).
  const [walkthroughFinished, setWalkthroughFinished] = useState(false)

  const { tfa } = useQuestionnaireTranslation()
  // v121.5.3: Prevent re-render crash after acknowledge — return null while navigating
  if (isNavigating) return null

  const NAVY = '#15354F'
  const GREEN_DARK = '#16A34A'
  const formatSpecialtyName = (name) => (name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  const caseInfo = `${formatCaseSpecialty(currentCase, t)} › ${currentCase.focusAreaName ? tfa(currentCase.focusAreaName) : ''}`

  // Find current encounter data
  const encounters = currentCase.encounters || []
  const currentEnc = encounters.find(e => e.encounterNumber === encounterNum || e.encounter_number === encounterNum) || {}
  
  // v125.0.3: Guard against rendering with incomplete encounter data
  // v126.0.0: Relaxed — only block if we truly have NO data. If encounterPhase is set,
  // we can render the pending/active state from case-level fields even without the encounters array.
  const hasEncounterPhaseData = encounterPhase && encounterPhase !== ''
  if (!currentEnc.id && encounterNum >= 2 && encounters.length === 0 && !hasEncounterPhaseData) {
    return (
      <div className="min-h-full bg-white flex flex-col">
        <CompactHeader stepNum={3} totalSteps={4} stepLabel="Diagnose" caseInfo={caseInfo}
          onBack={() => caseBack(navigate)} onNotification={() => navigate('/notifications')} />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <OrellaSpinner size={32} className="mx-auto mb-3" />
            <p className="text-sm text-gray-400">{t('diagnose.loadingEncounter')}</p>
          </div>
        </div>
      </div>
    )
  }
  
  // v121.5.2: Detect encounter state
  const currentCategory = currentEnc.category || currentEnc.appointment_category || ''
  // v121.8.0: Detect reopened state using encounter-aware logic.
  // Primary: current encounter's appointment has category 'reopened' and is pending
  const allAppointments = currentCase?.appointments || []
  const currentEncAppt = allAppointments.find(a => 
    (a.encounter_number || a.encounterNumber) === encounterNum && 
    a.appointment_category !== 'initial' &&
    !isOutsideAppointment(a)
  )
  const isEncApptPending = currentEncAppt?.status === 'pending'
  // v126.8.0: Bridge/reopen flags in appointmentRequest are only relevant during the
  // pending→confirmed→visit lifecycle (diagnose_pending, deliver_active).
  // Once the MD finalizes the encounter (diagnose_active, diagnose_acknowledged, complete),
  // these flags are consumed. Stale flags must NOT trigger the reopened/pending UI path.
  const isPastBridgePhase = ['diagnose_active', 'diagnose_acknowledged', 'complete'].includes(encounterPhase)
  const isTrulyTerminal = encounterPhase === 'complete' && ['completed', 'closed'].includes(currentCase?.status)
  // Legacy fallbacks for cases without encounter-tagged appointments
  const hasReopenedFlag = !isPastBridgePhase && (
    currentCase?.appointmentRequest?.reopened === true || currentCase?.appointmentRequest?.reopened === 'true'
    || currentCase?.appointmentRequest?.observationFinding === true  // v124.0.0: observation finding
  )
  const isReopenConfirmed = hasReopenedFlag && (currentCase?.appointmentRequest?.status === 'confirmed' || currentCase?.appointmentRequest?.status === 'scheduled')
  const isReopenStillPending = hasReopenedFlag && !isReopenConfirmed
  const followUpReopened = !isPastBridgePhase && (currentCase?.followUp?.reopened === true || currentCase?.followUp?.reopened === 'true')
  const statusIsFollowUp = currentCase?.status === 'in_follow_up'
  // v234.16.0: single source of truth — see utils/caseGating.js. Without an Orella MD there is
  // no managed Deliver to route to, so the acknowledged-state CTA goes to Case Home.
  const fuDeliverNeedsOrellaMD = deliverNeedsOrellaMDFn(currentCase)
  const phaseIsDeliver = encounterPhase === 'deliver_active'
  const isReopened = !isPastBridgePhase && (
    (isEncApptPending && encounterNum >= 2)
    || ((statusIsFollowUp || phaseIsDeliver) && currentCategory === 'complete') 
    || hasReopenedFlag 
    || (followUpReopened && (statusIsFollowUp || phaseIsDeliver))
  )
  // Terminal complete = truly closed, not reopened
  const isTerminalComplete = !isReopened && (currentCategory === 'complete' || encounterPhase === 'complete')
  
  // For terminal complete: show the LAST REAL encounter's AVS (the one that was finalized with "closed")
  // That's encounter N-1 (the one before the terminal marker)
  const lastRealEncNum = isTerminalComplete ? Math.max(encounterNum - 1, 1) : encounterNum
  const lastRealEnc = isTerminalComplete 
    ? (encounters.find(e => (e.encounterNumber || e.encounter_number) === lastRealEncNum) || {})
    : currentEnc

  // AVS source: use last real encounter's data, with legacy fallbacks
  // v123.1: Ensure all AVS values are strings (may be pre-parsed objects)
  // v171.14: Enhanced resolution — encounter-level AVS is primary, case-level is fallback
  const safeStr = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return v
    if (typeof v === 'object' && v.avsContent) return String(v.avsContent)
    return typeof v === 'object' ? JSON.stringify(v) : String(v)
  }
  
  // PROD-134 gamma: the stored AVS for this encounter (cases.avs) through the one reader,
  // else that encounter's own text (written by finalize_encounter), else the case-level chain.
  const encAvs = lastRealEnc.afterVisitSummary || lastRealEnc.after_visit_summary
  const caseAvs = currentCase?.diagnose?.followUpAVS || currentCase?.diagnose?.avsContent
  const caseLevelAvs = currentCase?.afterVisitSummary
  
  const displayAvs = readAvsText(currentCase, lastRealEncNum)
  
  console.log('[FollowUpDiagnose] AVS resolution:', {
    encounterNum,
    lastRealEncNum,
    encHasAVS: !!encAvs,
    encAVSLen: safeStr(encAvs).length,
    caseAvsLen: safeStr(caseAvs).length,
    caseLevelAvsLen: safeStr(caseLevelAvs).length,
    displayAvsLen: displayAvs.length,
    encHasCategory: !!(lastRealEnc.category || lastRealEnc.appointment_category),
    encPhase: lastRealEnc.phase,
    encFinalized: !!lastRealEnc.finalized,
    encountersCount: encounters.length,
    allEncNums: encounters.map(e => e.encounterNumber || e.encounter_number),
  })
  const parsedDisplayAvs = parseAVSIntoSections(displayAvs, t)

  // Initial encounter AVS (only relevant for encounters 3+, to show collapsed)
  const initialAvs = readAvsText(currentCase, 1)
  const parsedInitialAvs = parseAVSIntoSections(initialAvs, t)
  
  // Chat messages by phase
  // v125.2.0: Differentiate observation-bridge pending from standard follow-up pending
  // v126.6.0: Also differentiate post-confirmation pending (appointment confirmed, awaiting MD visit)
  // v126.8.0: Suppress stale flags when past the bridge phase (diagnose_active, acknowledged, complete)
  const isObservationBridge = !isPastBridgePhase && currentCase?.appointmentRequest?.observationFinding === true
  const isAppointmentConfirmed = !isPastBridgePhase && (currentCase?.appointmentRequest?.status === 'confirmed' || currentCase?.appointmentRequest?.status === 'scheduled')
  const chatMessages = {
    'diagnose_pending': isAppointmentConfirmed
      ? t('diagnose.followUpConfirmedRefMsg')
      : isObservationBridge
        ? t('diagnose.observationFindingRefMsg')
        : t('diagnose.preVisitPrepMsg'),
    'diagnose_active': t('diagnose.followUpAnalysisRefMsg'),
    'diagnose_acknowledged': t('diagnose.followUpCarePlanRefMsg'),
    'complete': t('diagnose.caseWrappedUpRefMsg'),
    'deliver_active': isReopenConfirmed
      ? t('diagnose.followUpApptConfirmedRefMsg')
      : isReopened 
        ? t('diagnose.followUpPendingRefMsg')
        : t('diagnose.followUpHeadToDeliverRefMsg'),
  }

  const isPending = encounterPhase === 'diagnose_pending'
  const isActive = encounterPhase === 'diagnose_active'
  const isAcknowledged = encounterPhase === 'diagnose_acknowledged' || encounterPhase === 'deliver_active'
  const isComplete = encounterPhase === 'complete' && !isReopened // truly closed only
  const isReopenedPending = isReopened // case reopened, awaiting follow-up
  // v121.8.3: For reopened cases, the encounter chain is: initial(1) → complete(2, terminal marker) → follow_up(3, from reopen).
  // The patient's perspective: Visit 1 = initial, Visit 2 = follow-up. Encounter 2 is internal-only (terminal marker).
  // So display encounterNum - 1 for reopened cases to skip the terminal marker in the count.
  // v126.0.0: EXCEPT for observation bridge — enc2 IS the bridged follow-up, no terminal marker to skip.
  // v126.8.0: Suppress stale flag when past the bridge phase
  const isObservationBridgeCase = !isPastBridgePhase && currentCase?.appointmentRequest?.observationFinding === true
  const displayVisitNum = (isReopened && !isObservationBridgeCase) ? Math.max(encounterNum - 1, 1) 
    : isTerminalComplete ? lastRealEncNum 
    : encounterNum
  const dotStep = (isAcknowledged || isComplete || isReopenedPending) ? 4 : 3

  // Handle acknowledge
  const handleAcknowledge = async () => {
    if (isAcknowledging || isNavigating) return
    setIsAcknowledging(true)
    try {
      await runOverlaySequence(setSubmitPhase, async () => {
        // PROD-529: the server answers first and is not caught here — a non-2xx (e.g. 409, the
        // visit is not signed) throws before any write, and the catch below goes home.
        // Held by scripts/check-acknowledge-refused.mjs.
        const result = await encountersAPI.acknowledge(currentCase.id, encounterNum)
        console.log(`[DiagnoseFollowUp] Acknowledged encounter ${encounterNum}, result:`, { success: !!result?.success, encounterPhase: result?.encounterPhase ?? null }) // PROD-903: no whole answer
        
        // v135.1.0 PA-A1+A2: Optimistic encounter array update
        // Update both flat fields AND encounters array so resolveActiveEncounter returns fresh data
        const storeUpdates = {
          diagnoseAcknowledged: true,
          diagnoseAcknowledgedAt: new Date().toISOString(),
        }
        if (result?.encounterPhase) storeUpdates.encounterPhase = result.encounterPhase
        if (result?.newEncounterNumber) storeUpdates.currentEncounter = result.newEncounterNumber
        if (result?.newEncounterCategory) storeUpdates.currentEncounterCategory = result.newEncounterCategory
        
        // Optimistically update encounters array: mark N as acknowledged/complete
        const existingEncs = currentCase.encounters || []
        const updatedEncs = existingEncs.map(e => {
          const eNum = e.encounter_number || e.encounterNumber
          if (eNum === encounterNum) {
            return { ...e, acknowledged: true, acknowledged_at: storeUpdates.diagnoseAcknowledgedAt, phase: 'complete' }
          }
          return e
        })
        storeUpdates.encounters = updatedEncs
        
        await updateAndSaveCase(storeUpdates)
        // PROD-903: field names only — `encounters` is the whole encounter array (reports, notes).
        console.log('[DiagnoseFollowUp] Store updated with encounter state + encounters array:', Object.keys(storeUpdates))
        
        // Background refresh to get full server state (encounters, referrals, etc.)
        try { await loadCases() } catch (e) { /* non-blocking */ }
      }, () => {
        setIsNavigating(true)
        navigate('/case/home')
      })
    } catch (err) {
      console.error('[DiagnoseFollowUp] Acknowledge error:', err)
      setIsNavigating(true)
      navigate('/case/home')
    }
  }

  return (
    <div className="min-h-full bg-gray-50 flex flex-col">
      <ChatKeyframes />
      <CompactHeader
        stepNum={dotStep}
        totalSteps={3}
        stepLabel={t('diagnose.diagnoseVisitLabel', { num: displayVisitNum })}
        caseInfo={caseInfo}
        onBack={() => caseBack(navigate)}
        onNotification={() => navigate('/notifications')}
      />
      <OrellaChatBubble
        message={chatMessages[encounterPhase] || chatMessages['diagnose_pending']}
        animationKey={`diagnose-followup-${encounterPhase}-${currentCase?.id}`}
        onComplete={() => setShowContent(true)}
      />

      <div
        className="flex-1 px-4 pt-2 pb-4 overflow-auto space-y-3"
        style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}
      >
        {/* ===== PENDING: Waiting for MD ===== */}
        {/* v126.0.0: Generic pending card only shows for standard follow-up pending (no observation bridge).
            Observation bridge uses the reopened pending UI below (amber banner + collapsible initial AVS).
            v225.24.0: Render the SAME doctor + confirmed-appointment view as the initial pending
            state (shared PendingConsultCard) so the two flows match. */}
        {isPending && !isReopened && (() => {
          const fuDoc = currentCase.doctor || currentCase.sentToDoctorData || null
          const docName = currentCase.sentToDoctorName
            || (fuDoc ? clinicianName(withCaseClinician(fuDoc, currentCase)) : '')
            || currentEncAppt?.doctor_name
            || 'Your Doctor'
          const nameForInitials = (docName || '').replace(/^Dr\.\s*/i, '')
          const docInitials = (
            (fuDoc?.first_name?.[0] || fuDoc?.firstName?.[0] || currentEncAppt?.doctor_first_name?.[0] || nameForInitials?.[0] || '')
            + (fuDoc?.last_name?.[0] || fuDoc?.lastName?.[0] || currentEncAppt?.doctor_last_name?.[0] || '')
          ).toUpperCase() || '?'
          const docPhoto = fuDoc?.profile_photo || currentEncAppt?.doctor_photo || null
          const docSpecialty = fuDoc?.specialty || currentEncAppt?.doctor_specialty || ''
          const docPracticeName = fuDoc?.practice_name || currentEncAppt?.practice_name || ''
          const apptDateISO = currentEncAppt?.date || currentEncAppt?.scheduled_date || ''
          const apptTime = currentEncAppt?.time || currentEncAppt?.scheduled_time || ''
          const apptType = currentEncAppt?.type || currentEncAppt?.appointment_type || 'in_person'
          const apptStatus = currentEncAppt?.status
          const isApptConfirmed = apptStatus === 'confirmed' || apptStatus === 'scheduled'
          return (
            <>
              <PendingConsultCard
                docName={docName}
                docSpecialty={docSpecialty}
                docPracticeName={docPracticeName}
                docPhoto={docPhoto}
                docInitials={docInitials}
                statusLabel={isApptConfirmed ? t('diagnose.accepted') : t('diagnose.reviewing')}
                statusTone="blue"
                apptTitle={isApptConfirmed ? t('diagnose.followUpConfirmed') : t('diagnose.followUpRequested')}
                apptDateISO={apptDateISO}
                apptTime={apptTime}
                apptType={apptType}
                isApptConfirmed={isApptConfirmed}
                joinTarget={videoJoinTarget(currentEncAppt)}
                onJoinCheckIn={() => navigate(joinCheckInPath(currentCase?.id, currentEncAppt?.id))}
                onReschedule={() => navigate('/case/home', { state: { openReschedule: true } })}
                fallbackTitle={t('diagnose.awaitingFollowUpAnalysis')}
                fallbackBody={t('diagnose.followUpFallbackBody')}
                t={t}
              />

              {/* Upload additional documents — always links to Detect so the patient
                  can add documents to the case at any time (parity with initial flow). */}
              <button
                onClick={() => navigate('/case/detect')}
                className="w-full flex items-center gap-2 px-3.5 py-3 bg-gray-50 border-[1.5px] border-dashed border-gray-200 rounded-xl text-[12px] font-semibold text-[#46A8F9] hover:bg-[#E9F8FF] hover:border-[#A7D9FC] transition-all"
              >
                <Upload size={15} />
                {t('diagnose.uploadAdditionalDocs')}
                <ChevronRight size={14} className="text-gray-300 ml-auto" />
              </button>

              {/* Check for updates */}
              <button
                onClick={handleFollowUpRefresh}
                disabled={isRefreshing}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold text-gray-400"
              >
                {isRefreshing ? <OrellaSpinner size={16} tone="inherit" /> : <RefreshCw size={13} />}
                {t('diagnose.checkForUpdates')}
              </button>
            </>
          )
        })()}

        {/* ===== ACTIVE/ACKNOWLEDGED/COMPLETE/REOPENED: AVS Content ===== */}
        {/* v126.0.0: Also include isPending + isReopened (observation bridge pending state) */}
        {(isActive || isAcknowledged || isComplete || isReopenedPending || (isPending && isReopened)) && (
          <>
            {/* Acknowledged/Complete banner */}
            {(isAcknowledged || isComplete) && (
              <div className="bg-green-50 border border-green-200 rounded-[12px] p-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Lock size={14} className="text-green-600" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[12.5px] font-semibold text-green-700">
                      {isTerminalComplete ? t('diagnose.carePlanCompleteClosedTitle') : t('diagnose.followUpAcknowledgedTitle')}
                    </p>
                  </div>
                  <Check size={18} className="text-green-500 flex-shrink-0" strokeWidth={2.5} />
                </div>
              </div>
            )}
            
            {/* Reopened banner — pending or confirmed */}
            {/* v126.0.0: Skip pending banner for observation bridge (the card below covers it).
                v126.3.0: But DO show the confirmed green banner when appointment is confirmed. */}
            {isReopenedPending && (!isObservationBridgeCase || isReopenConfirmed) && (
              <div className="bg-amber-50 border-amber-200 border rounded-[12px] p-3">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full ${isReopenConfirmed ? 'bg-green-100' : 'bg-amber-100'} flex items-center justify-center flex-shrink-0`}>
                    {isReopenConfirmed 
                      ? <Check size={14} className="text-green-600" strokeWidth={2.5} />
                      : <Clock size={14} className="text-amber-600" strokeWidth={2.5} />
                    }
                  </div>
                  <div className="flex-1">
                    <p className="text-[12.5px] font-semibold text-amber-700">
                      {isReopenConfirmed ? t('diagnose.apptConfirmedAwaitingTitle') : t('diagnose.followUpAwaitingConfirmTitle')}
                    </p>
                    <p className="text-[10.5px] mt-0.5 text-amber-500">
                      {isReopenConfirmed ? t('diagnose.followUpWillConductShare') : t('diagnose.followUpWillConfirmShortly')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ TERMINAL COMPLETE: Show last real visit AVS only (locked) ═══ */}
            {isTerminalComplete && (
              <div className={isComplete ? 'opacity-85' : ''}>
                {parsedDisplayAvs && parsedDisplayAvs.length > 0 ? (
                  <AVSChecklist sections={parsedDisplayAvs} gated={false} t={t} />
                ) : displayAvs ? (
                  <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5">
                    <div className="text-[14px] text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                      {displayAvs}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5 text-center">
                    <p className="text-[14px] text-gray-400">{t('diagnose.consultSummaryAvailable')}</p>
                  </div>
                )}
              </div>
            )}

            {/* ═══ REOPENED PENDING: Awaiting card + Initial Visit Diagnosis only ═══ */}
            {isReopenedPending && (
              <>
                {/* Pending/Confirmed follow-up card */}
                {isReopenConfirmed ? (
                  <div className="bg-white rounded-[14px] border-[1.5px] border-amber-200 p-5 text-center">
                    <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                      <Clock size={28} className="text-amber-500" />
                    </div>
                    <h3 className="text-[15px] font-bold text-gray-800 mb-1">{t('diagnose.apptConfirmedAwaiting')}</h3>
                    <p className="text-[12.5px] text-gray-400 leading-relaxed">
                      {t('diagnose.followUpReopenConfirmedBody')}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-[14px] border-[1.5px] border-amber-200 p-5 text-center">
                    <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                      <Clock size={28} className="text-amber-500" />
                    </div>
                    <h3 className="text-[15px] font-bold text-gray-800 mb-1">{t('diagnose.awaitingFollowUpVisit')}</h3>
                    <p className="text-[12.5px] text-gray-400 leading-relaxed">
                      {t('diagnose.followUpReopenPendingBody')}
                    </p>
                  </div>
                )}

                {/* Collapsible: Initial Visit Diagnosis (links to visit 1 AVS) */}
                <button
                  onClick={() => setShowPrevious(!showPrevious)}
                  className="w-full bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Eye size={16} className="text-gray-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-[13px] font-bold text-gray-700">{t('diagnose.initialVisitDiagnosis')}</p>
                      <p className="text-[11px] text-gray-400">{t('diagnose.viewOriginalResults')}</p>
                    </div>
                  </div>
                  {showPrevious 
                    ? <ChevronDown size={18} className="text-gray-400" />
                    : <ChevronRight size={18} className="text-gray-400" />
                  }
                </button>

                {showPrevious && (
                  <div className="opacity-70 pl-2 border-l-2 border-gray-200 ml-4 space-y-2">
                    {parsedInitialAvs && parsedInitialAvs.length > 0 ? (
                      parsedInitialAvs.map((section, idx) => (
                        <AVSSection key={`initial-${idx}`} {...section} />
                      ))
                    ) : initialAvs ? (
                      <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5">
                        <div className="text-[14px] text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                          {initialAvs}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-[12px] border border-gray-200 p-3 text-center">
                        <p className="text-[11px] text-gray-400">{t('diagnose.noInitialSummary')}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ═══ REAL FOLLOW-UP: Initial collapsed at top, follow-up AVS open below ═══ */}
            {!isTerminalComplete && !isReopenedPending && (
              <>
                {/* Collapsible Initial Visit AVS */}
                <button
                  onClick={() => setShowPrevious(!showPrevious)}
                  className="w-full bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Eye size={16} className="text-gray-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-[13px] font-bold text-gray-700">{t('diagnose.initialVisitDiagnosis')}</p>
                      <p className="text-[11px] text-gray-400">{t('diagnose.viewOriginalResults')}</p>
                    </div>
                  </div>
                  {showPrevious 
                    ? <ChevronDown size={18} className="text-gray-400" />
                    : <ChevronRight size={18} className="text-gray-400" />
                  }
                </button>

                {showPrevious && (
                  <div className="opacity-70 pl-2 border-l-2 border-gray-200 ml-4 space-y-2">
                    {parsedInitialAvs && parsedInitialAvs.length > 0 ? (
                      parsedInitialAvs.map((section, idx) => (
                        <AVSSection key={`prev-${idx}`} {...section} />
                      ))
                    ) : initialAvs ? (
                      <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5">
                        <div className="text-[14px] text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                          {initialAvs}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-[12px] border border-gray-200 p-3 text-center">
                        <p className="text-[11px] text-gray-400">{t('diagnose.noInitialSummary')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Visit label for follow-up AVS */}
                <div className="flex items-center gap-2 px-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: NAVY }} />
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: NAVY }}>{t('diagnose.followUpVisitLabel')} {displayVisitNum > 2 ? `#${displayVisitNum - 1}` : ''}</span>
                </div>

                {/* Follow-up AVS sections — walkthrough on first view, flat after acknowledge */}
                <div className={(isAcknowledged || isComplete) ? 'opacity-85' : ''}>
                  {parsedDisplayAvs && parsedDisplayAvs.length > 0 ? (
                    <AVSReview
                      avsText={displayAvs}
                      caseId={currentCase?.id}
                      gated={isActive}
                      onAllOpened={() => setWalkthroughFinished(true)}
                      t={t}
                    />
                  ) : displayAvs ? (
                    <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5">
                      <div className="text-[14px] text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                        {displayAvs}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5 text-center">
                      <p className="text-[14px] text-gray-400">{t('diagnose.followUpSummaryAvailable')}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ===== FOOTER ===== */}
      <div className="p-4 pb-5 flex-shrink-0 bg-white border-t border-gray-100" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
        {isActive && (() => {
          // v217.1.4: Gate on walkthrough completion. If there are no
          // parsed sections (flat AVS text or empty state), there's no
          // walkthrough to complete, so the CTA is active immediately.
          const reviewReady = !parsedDisplayAvs || parsedDisplayAvs.length === 0 || walkthroughFinished
          return (
            <button
              onClick={reviewReady ? handleAcknowledge : undefined}
              disabled={isAcknowledging || !reviewReady}
              className={`w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 ${
                !isAcknowledging && reviewReady ? 'breathe-green' : ''
              } ${reviewReady ? '' : 'opacity-50 cursor-not-allowed'} ${
                isAcknowledging ? 'opacity-50' : ''
              }`}
              style={{ backgroundColor: GREEN_DARK }}
              aria-disabled={!reviewReady}
            >
              {isAcknowledging ? t('common.saving') : <><Check size={18} strokeWidth={2.5} /> {t('diagnose.iveReviewedThis')}</>}
            </button>
          )
        })()}
        {(isAcknowledged || isComplete) && (
          <>
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Lock size={11} className="text-gray-300" />
              <span className="text-[10.5px] text-gray-300 font-medium">{t('diagnose.sectionLocked')}</span>
            </div>
            {/* v234.15.2: MD-agnostic case — no Orella MD, so Deliver is locked. Route to Case
                Home instead of a Deliver step that cannot be entered. */}
            {fuDeliverNeedsOrellaMD ? (
              <button
                onClick={() => navigate('/case/home')}
                className="w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2"
                style={{ backgroundColor: NAVY }}
              >
                {t('diagnose.backToCaseHome') || 'Back to Case Home'}
              </button>
            ) : (
              <button
                onClick={() => navigate('/case/deliver')}
                className="w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2"
                style={{ backgroundColor: NAVY }}
              >
                {t('diagnose.goToDeliver')}
              </button>
            )}
          </>
        )}
        {isPending && !isReopened && (
          <div className="space-y-3 flex flex-col items-center">
            {/* v179.1.3: Ask Orella — CTA chip with shimmer animation */}
            <AskOrellaChip
              onClick={() => openAgent?.()}
              label={t('diagnose.askOrella') || 'Ask Orella'}
            />
            {/* Secondary: Back to Case Home */}
            <button 
              onClick={() => navigate('/case/home')} 
              className="py-3 rounded-[14px] text-[13px] font-semibold cursor-pointer border-[1.5px] border-gray-200 bg-white text-[#15354F] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors w-full"
            >
              {t('diagnose.backToCaseHome') || 'Back to Case Home'}
            </button>
          </div>
        )}
        {isReopenedPending && (
          <button
            onClick={() => navigate('/case/home')}
            className="w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 cursor-pointer border-none"
            style={{ backgroundColor: NAVY }}
          >
            ← {t('diagnose.backToCaseHome')}
          </button>
        )}
      </div>

      <SubmitOverlay
        phase={submitPhase}
        processingTitle={t('diagnose.savingAcknowledgment')}
        processingSubtitle={t('diagnose.updatingYourCarePlan')}
        successTitle={t('diagnose.diagnoseReviewed')}
        successSubtitle={t('diagnose.headingToYourCarePlan')}
      />
    </div>
  )
}


export default function DiagnoseScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const showShareCodeView = location.state?.showShareCode === true
  const { currentCase, updateCase, updateAndSaveCase, loadCases } = useCaseStore()
  const checkInProfile = useProfileStore((sel) => sel.profile)
  const { t } = useI18n()
  const { tfa } = useQuestionnaireTranslation()
  const { suppressFab, openAgent } = useAgent()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [isAcknowledging, setIsAcknowledging] = useState(false)
  const [submitPhase, setSubmitPhase] = useState(null)
  const [showContent, setShowContent] = useState(false)
  const [walkthroughFinished, setWalkthroughFinished] = useState(false)

  // External upload state (A-29)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [isSubmittingUpload, setIsSubmittingUpload] = useState(false)
  const fileInputRef = useRef(null)

  // Visit Prep flow state (A-28)
  const [extSubState, setExtSubState] = useState('generating') // generating | disclaimer | ready | upload
  const autoGenStartedRef = useRef(false)
  const [visitPrepCode, setVisitPrepCode] = useState('')
  const [visitPrepExpiration, setVisitPrepExpiration] = useState('')
  const [isGeneratingPrep, setIsGeneratingPrep] = useState(false)
  const [generateError, setGenerateError] = useState(null)
  const [profileMissing, setProfileMissing] = useState(null) // s95: sections missing before a share
  
  // AI analysis state (A-30)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState(null)
  const [analysisProgress, setAnalysisProgress] = useState(0) // 0-100 for progress animation
  
  // Cancel/revoke modal state
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelModalSource, setCancelModalSource] = useState('ready') // ready | upload
  // ★★ NOTE: cancelModalSource is SET in two places and READ in none. Write-only
  //   state that survived the screen it described. Not this wave's to remove.
  const [isRevoking, setIsRevoking] = useState(false)
  // v225.15.0 (C-A-174): unified "Change or cancel sharing" sheet for the share-code
  // sequence — replaces the single-action Cancel→Orella confirm with one sheet that
  // offers switch-to-Orella-MD (primary), regenerate code, and withdraw.
  const [showManageSheet, setShowManageSheet] = useState(false)
  // s99 D476: "Cancel & share with another doctor" on the outside-doctor upload screen.
  const [showSwitchSheet, setShowSwitchSheet] = useState(false)
  const [showReportViewer, setShowReportViewer] = useState(false) // s99 D477
  const viewAfterAgreeRef = useRef(false)
  const [switching, setSwitching] = useState(false)
  // ★★ v242.207.0 — the share-code link opens THIS panel directly: "Your Doctors",
  //   then "Orella doctors". Alex, 2026-09-23: opening it has NO server-side effect
  //   and closing it leaves the share code alive. Picking a doctor hands over to
  //   ShareScreen's confirm-doctor step (the slot picker), and the code dies only
  //   when that send succeeds. That is done server-side by send_case_to_doctor
  //   (PROD-179), which is why nothing on this path calls revokeShareCode.
  const [showDoctorPanel, setShowDoctorPanel] = useState(false)
  const [doctorPanel, setDoctorPanel] = useState({ loading: false, yourDoctors: [], orellaDoctors: [] })
  const [isRegenerating, setIsRegenerating] = useState(false)
  // ★★★ P11 (TEST 1.08) — A SHARE CODE IS NEVER KILLED WITHOUT ASKING. "Generate new share
  //   code" and "Send to a doctor on orella" revoked the live code on the first tap: the QR
  //   code, the visit pass PDF and any link already sent died with nothing said. shareKill
  //   names the kill waiting for the patient's confirmation (null | 'regenerate' | 'orella').
  //   The handlers run their kill ONLY when called with the literal `true`, because every
  //   onClick hands them a truthy SyntheticEvent. shareKillInFlightRef makes a double tap on
  //   Confirm one kill and one mint, not two. scripts/check-share-code-warning.mjs holds it.
  const [shareKill, setShareKill] = useState(null)
  const shareKillInFlightRef = useRef(false)
  // ★★ P11 (TEST 1.08 "nobody is told", D6) — what a confirmed regenerate actually did:
  //   { kind: 'replaced', oldCode, newCode } or { kind: 'failed', oldCode } when the old code
  //   was revoked and no new one could be made. Rendered as a dismissible banner.
  const [shareNotice, setShareNotice] = useState(null)

  // v138.1.0: Withdraw case modal state (Orella path — pending/confirmed)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [showCheckIn, setShowCheckIn] = useState(false)
  const checkInHeldSlot = confirmedCheckInSlot(currentCase) // PROD-1007 (null unless a reschedule is held and the old slot is confirmed)
  const [showQRModal, setShowQRModal] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)

  // v177.4.4: Auto-open check-in when navigated from My Visits
  useEffect(() => {
    if (location.state?.openCheckIn) {
      setShowCheckIn(true)
      // Clear the flag so it doesn't re-trigger on re-render
      window.history.replaceState({}, '')
    }
  }, [location.state?.openCheckIn])

  // v200.3: Auto-open withdraw modal when navigated from Visits cancel button
  useEffect(() => {
    if (location.state?.openWithdraw) {
      setShowWithdrawModal(true)
      window.history.replaceState({}, '')
    }
  }, [location.state?.openWithdraw])

  // v155.0.6: Suppress FAB during transition/animation states
  useEffect(() => {
    const shouldSuppress = extSubState === 'generating' || isAnalyzing || isSubmittingUpload || !!submitPhase
    suppressFab?.(shouldSuppress)
    return () => suppressFab?.(false)
  }, [extSubState, isAnalyzing, isSubmittingUpload, submitPhase])

  // ★★★ P11 (TEST 1.08, D2) — the manage sheet's "Withdraw the case" row ran this on the
  //   tap and killed the share code with it. It now opens the EXISTING withdraw modal, and
  //   only that modal's Confirm, which passes the literal `true`, withdraws.
  const handleWithdrawCase = async (confirmed) => {
    if (confirmed !== true) { setShowWithdrawModal(true); return }
    if (shareKillInFlightRef.current) return
    shareKillInFlightRef.current = true
    setIsWithdrawing(true)
    // TEST 3.17 (patient push 3a): where to land is read from the case BEFORE it is cleared
    const caseBefore = currentCase
    try {
      // ★ v242.106.0 — the field list and the revoke call moved to
      //   utils/withdrawCase.js so Case Home's new cancel link runs THIS
      //   withdrawal, not a second one that happens to match today.
      await withdrawCaseFromDoctor(currentCase.id, updateAndSaveCase, caseBefore)
      console.log('[DiagnoseScreen] ✅ Case withdrawn from doctor')
      setShowWithdrawModal(false)
      setShowManageSheet(false)
      setIsWithdrawing(false)
      navigate(withdrawDestination(caseBefore), { replace: true })
    } catch (err) {
      console.error('[DiagnoseScreen] Withdraw failed:', err)
      setIsWithdrawing(false)
      setShowWithdrawModal(false)
    } finally {
      shareKillInFlightRef.current = false
    }
  }

  // External doctor lookup state
  const [savedDoctor, setSavedDoctor] = useState(() => {
    const doc = currentCase?.externalDoctor || currentCase?.external_doctor
    return doc || null
  })

  const handleSaveDoctor = async (doctorData) => {
    setSavedDoctor(doctorData)
    try {
      await updateAndSaveCase({
        externalDoctor: doctorData,
        external_doctor: doctorData,
      })
      const { casesAPI } = await import('../../services/api')
      await casesAPI.update(currentCase.id, { external_doctor: doctorData })
      console.log('[DiagnoseScreen] ✅ External doctor saved')
    } catch (err) {
      console.warn('[DiagnoseScreen] Failed to save external doctor:', err)
    }
  }

  const isAcknowledged = currentCase?.diagnoseAcknowledged || false
  const isExternalPath = currentCase?.doctorSource === 'external' || currentCase?.doctor_source === 'external'

  // v234.16.0: single source of truth — see utils/caseGating.js. Used to suppress the
  // "Go to Deliver" exit on the acknowledged/locked AVS screen (no managed Deliver to route to).
  const deliverNeedsOrellaMD = deliverNeedsOrellaMDFn(currentCase)

  // v129.1.0: ALL useEffects MUST be called before any conditional returns (React hooks rules).
  // Moved from below to prevent hooks ordering crash when isFollowUpDiagnose flips true.
  
  // External path sub-state initialization
  useEffect(() => {
    if (isExternalPath) {
      const existingCode = currentCase?.shareCode || currentCase?.share_code
      const hasNote = !!(currentCase?.externalDoctorNote || currentCase?.external_doctor_note)
      if (hasNote) {
        setExtSubState('upload')
      } else if (showShareCodeView && existingCode) {
        setVisitPrepCode(existingCode)
        setVisitPrepExpiration(currentCase?.shareExpiration || '')
        setExtSubState('ready')
      } else if (existingCode) {
        // ★★ s114 B5 (Dr. Rami 2026-10-22 build-80 bugs, TestFlight 13: "Upload Doctor's Note" on a case shared from
        //   the hub showed the share disclaimer and then the OLD code screen). Since D468 the hub mints the code and
        //   records the agreement only when the patient opens View PDF, so a hub-shared case arrives here with a code
        //   and no agreement. A case that already has a code goes STRAIGHT to the note upload; the agreement still
        //   gates the PDF (View routes through the disclaimer with viewAfterAgreeRef and returns to upload).
        //   The code screen ('ready') is only the explicit showShareCodeView route and a code minted here.
        setVisitPrepCode(existingCode)
        setVisitPrepExpiration(currentCase?.shareExpiration || '')
        setExtSubState('upload')
      }
    }
  }, [isExternalPath, currentCase?.shareCode, currentCase?.share_code, showShareCodeView])

  // ★★★ THE PREP GATE IS GONE, SO ARRIVAL IS THE TRIGGER. extSubState now INITS to
  //   'generating', and a state that only RENDERS a spinner is a success the code never
  //   verified (D438). This is what makes the spinner true.
  //   ★★ THE REF IS LOAD-BEARING: this effect's deps include shareCode, which
  //   handleGenerateVisitPrep itself writes, so without the guard the second run would
  //   mint a SECOND share code for the same case.
  //   ★ Runs only for a case with no code and no note yet; every other arrival is
  //   already routed by the rehydration effect above.
  useEffect(() => {
    if (!isExternalPath) return
    if (autoGenStartedRef.current) return
    const existingCode = currentCase?.shareCode || currentCase?.share_code
    const hasNote = !!(currentCase?.externalDoctorNote || currentCase?.external_doctor_note)
    if (existingCode || hasNote) return
    autoGenStartedRef.current = true
    handleGenerateVisitPrep()
  }, [isExternalPath, currentCase?.shareCode, currentCase?.share_code])

  useEffect(() => { 
    window.scrollTo(0, 0)
    const sw = document.querySelector('.scroll-wrapper')
    if (sw) sw.scrollTop = 0
    // Delayed scroll reset — ensures it fires after animations/transitions complete
    const t = setTimeout(() => {
      window.scrollTo(0, 0)
      const sw2 = document.querySelector('.scroll-wrapper')
      if (sw2) sw2.scrollTop = 0
    }, 100)
    return () => clearTimeout(t)
  }, [])

  // v221.4.7 — Scroll to top on EVERY extSubState transition (was only 'generating' before).
  // Dr. Rami: each share-flow screen must open at the top of the page, even though the
  // Orella dialogue bubble at top suggests visual continuity.
  useEffect(() => {
    if (!extSubState) return
    window.scrollTo({ top: 0, behavior: 'instant' })
    const sw = document.querySelector('.scroll-wrapper')
    if (sw) sw.scrollTop = 0
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'instant' })
      const sw2 = document.querySelector('.scroll-wrapper')
      if (sw2) sw2.scrollTop = 0
    })
  }, [extSubState])
  
  // Auto-refresh if case is shared/claimed to check for completion
  useEffect(() => {
    if (currentCase && !hasLoadedOnce) {
      const shouldRefresh = currentCase.status === 'claimed' || 
        currentCase.status === 'completed' ||
        (!currentCase.isShared && !currentCase.shareCode && currentCase.status === 'shared')
      if (shouldRefresh) {
        console.log('[DiagnoseScreen] Auto-refreshing for status:', currentCase.status)
        // Inline refresh to avoid function ordering issues
        setIsRefreshing(true)
        loadCases().catch(err => console.error('Failed to refresh:', err)).finally(() => setIsRefreshing(false))
      }
      setHasLoadedOnce(true)
    }
  }, [currentCase?.status, hasLoadedOnce])

  // v177.2.0: Poll for status changes while in pending state (picks up doctor acceptance)
  // ★★★ TEST 1.15 — setInterval CANNOT back off: its period is fixed at the moment it is
  //   armed. A self-rescheduling setTimeout is what lets the gap grow, so the flat 15 s
  //   interval is gone rather than parameterised. See the POLL_* constants at module scope
  //   for the budget and the measurement behind it.
  useEffect(() => {
    const isPendingState = currentCase?.status === 'shared' || currentCase?.status === 'sent'
    if (!isPendingState) return

    let timer = null
    let stopped = false
    let delay = POLL_FIRST_MS
    let lastFetchAt = Date.now()   // the first tick waits the full interval, as before

    const tick = async () => {
      if (stopped) return
      // ★ HIDDEN MEANS NOTHING AT ALL — no fetch and no re-arm. The visibility handler
      //   below is what starts it again, so a backgrounded tab costs zero rather than a
      //   timer that keeps waking to decide it has nothing to do.
      if (document.visibilityState !== 'visible') { timer = null; return }
      const since = Date.now() - lastFetchAt
      if (since < POLL_FIRST_MS) { timer = setTimeout(tick, POLL_FIRST_MS - since); return }
      lastFetchAt = Date.now()
      console.log(`[DiagnoseScreen] Polling for status update (next in ${Math.round(delay / 1000)}s)...`)
      // PROD-270: IF the server refuses this poll (403) it ends here — no next tick, and the
      // visibility handler below cannot restart it. Any other failure keeps polling.
      // ★ PROD-424 (b), patient push 3a: this is a guard, not a claim that a patient's poll is
      //   ever refused. Nothing here asserts when the API answers a patient's GET /cases with
      //   a 403, so nothing here says it does.
      try { await loadCases({ rethrowRefusal: true }) } catch (e) {
        if (pollRefused(e)) { stopped = true; timer = null; return }
        /* a transient poll failure is not a screen error */
      }
      if (stopped) return
      delay = Math.min(Math.round(delay * POLL_BACKOFF), POLL_CEILING_MS)
      timer = setTimeout(tick, delay)
    }

    // ★ Same shape as useNotificationStore.startPolling: visibilityState, and a debounce so
    //   flicking between tabs cannot turn into a fetch per switch.
    const onVisibility = () => {
      if (stopped) return
      if (timer) { clearTimeout(timer); timer = null }
      if (document.visibilityState !== 'visible') return
      delay = POLL_FIRST_MS
      const since = Date.now() - lastFetchAt
      timer = setTimeout(tick, since > POLL_DEBOUNCE_MS ? 0 : POLL_DEBOUNCE_MS - since)
    }
    document.addEventListener('visibilitychange', onVisibility)

    timer = setTimeout(tick, delay)
    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [currentCase?.status])

  // ============ ENCOUNTER 2+ FOLLOW-UP MODE (v120) ============
  const encounterNum = currentEncounterNumber(currentCase)
  const encounterPhase = currentCase?.encounterPhase || ''
  // v124.4.0: Detect observation encounter — observation has NO Diagnose phase
  // When in observation, DiagnoseScreen shows the acknowledged initial visit AVS (locked)
  // v171.12.0: ONLY check the active encounter's category, not case status.
  // Case status 'in_observation' means the DISPOSITION is observation (N+2 will be observation),
  // but the current encounter (N+1) may be a follow_up that was just finalized with an AVS to review.
  const activeEncounters = currentCase?.encounters || []
  const activeEnc = activeEncounters.find(e => (e.encounter_number || e.encounterNumber) === encounterNum) || {}
  const isObservationEnc = activeEnc.category === 'observation'
  
  // v140.6: CRITICAL FIX — Diagnose stays on N's content until N+1 is FINALIZED.
  // The follow-up Diagnose view should only render when the follow-up encounter has actual
  // diagnosis data from the doctor (AVS, consult note). During deliver_active, the follow-up
  // hasn't happened yet — Diagnose must continue showing the initial consult's diagnosis.
  //
  // State transitions for Diagnose:
  //   N finalized, patient acknowledges → encounterNum=2, phase=deliver_active → show N's AVS
  //   Patient schedules follow-up → encounterNum=2, phase=deliver_active → show N's AVS
  //   Follow-up visit happens, MD finalizes → encounterNum=2, phase=diagnose_active → show N+1's AVS
  //   Patient acknowledges follow-up → encounterNum=2, phase=diagnose_acknowledged → show N+1's AVS (locked)
  //
  // Key insight: deliver_active and diagnose_pending (without data) mean the follow-up visit
  // hasn't produced results yet. Only diagnose_active/acknowledged/complete have real N+1 data.
  
  const followUpHasDiagnoseData = (() => {
    if (encounterNum < 2) return false
    // Check if the current encounter has any finalized diagnosis content
    const enc = activeEncounters.find(e => (e.encounter_number || e.encounterNumber) === encounterNum) || {}
    const hasAVS = !!(enc.afterVisitSummary || enc.after_visit_summary)
    // ★ PROD-1077: a held note is a result too (the API blanks its text; the AVS may still be empty).
    //   Only on a SIGNED row: md can write the hold before signing, or onto the unsigned N+1 row.
    const hasConsultNote = !!(enc.consultNote || enc.consult_note) || (enc.finalized === true && isEncounterNoteHeld(enc))
    // Also check case-level follow-up diagnose fields
    const hasFollowUpAVS = !!(currentCase?.diagnose?.followUpAVS || currentCase?.diagnose?.avsContent)
    const hasFollowUpNote = !!(currentCase?.diagnose?.followUpConsultNote)
    return hasAVS || hasConsultNote || hasFollowUpAVS || hasFollowUpNote
  })()
  
  const isFollowUpDiagnose = encounterNum >= 2 && !isObservationEnc && (
    // Phase has actual diagnosis data from the doctor
    (['diagnose_active', 'diagnose_acknowledged', 'complete'].includes(encounterPhase) && followUpHasDiagnoseData) ||
    // diagnose_pending WITH data (MD finalized but patient hasn't acknowledged yet — rare timing)
    (encounterPhase === 'diagnose_pending' && followUpHasDiagnoseData)
  )

  if (isFollowUpDiagnose && currentCase) {
    return (
      <FollowUpDiagnoseView
        currentCase={currentCase}
        encounterNum={encounterNum}
        encounterPhase={encounterPhase}
        navigate={navigate}
        updateAndSaveCase={updateAndSaveCase}
        loadCases={loadCases}
        t={t}
      />
    )
  }
  
  // Detect if external case was claimed by an Orella doctor
  // NOTE: Cannot reference `isCompleted` here — it's defined later in the render.
  // Inline the status checks to avoid TDZ (Temporal Dead Zone) crash.
  const externalClaimedByOrella = isExternalPath && (currentCase?.status === 'claimed' || currentCase?.status === 'in_progress' || currentCase?.status === 'completed' || currentCase?.status === 'in_follow_up') && !!(currentCase?.doctorId || currentCase?.doctor_id)
  
  // Preserve share code for display even after claim
  const displayShareCode = currentCase?.shareCode || currentCase?.share_code || visitPrepCode

  // Generate Visit Prep — calls casesAPI.share() AND AI pre-visit summary
  // ★★★ L10 A7 (TEST 1.08; Alex L10 D1 = Track A) — `opts.renew === true` asks the SAME
  //   call site above to extend the live code in place instead of minting a replacement.
  //   A confirmed renew returns early (no AI hand-off, no disclaimer step: the patient
  //   never left the manage sheet and nothing to re-agree to changed). A REFUSED renew
  //   (409 share_code_not_renewable) is rethrown so handleRegenerateCode's kill-then-mint
  //   fallback runs; every other outcome — including an older API that does not recognise
  //   `renew` and just mints, carrying no `renewed` field at all — falls through to the
  //   unchanged mint flow below, which is exactly "treat the response as a mint" (D-none:
  //   no client should ever mint twice for one tap). Gate: scripts/check-share-renew.mjs.
  const handleGenerateVisitPrep = async (opts = {}) => {
    const isRenew = opts.renew === true
    const isReplacement = opts.replacing === true
    // ★ s95: a share code is a share. Insurance (or self-pay), allergies and medications
    //   must be on file first — sign-up no longer asks for them. A renew re-dates a code
    //   that already exists, and a replacement is minted AFTER the old code was revoked
    //   (refused renew) — stopping either would leave a case that was already shared with
    //   no working code at all. Only a FIRST share is stopped here.
    if (!isRenew && !isReplacement) {
      const missing = missingBeforeShare(useProfileStore.getState().profile)
      if (missing.length) {
        setProfileMissing(missing)
        setExtSubState('generating')
        return
      }
      setProfileMissing(null)
    }
    setIsGeneratingPrep(true)
    setGenerateError(null)
    if (!isRenew) setExtSubState('generating')
    try {
      const { casesAPI, aiAPI } = await import('../../services/api')

      // Step 1: Generate share code — stamp doctor_source='external' atomically with
      // the code (v227.7.3) so the outside-MD marker durably persists and DiagnoseScreen
      // doesn't revert to the internal flow on reload.
      const response = await casesAPI.generateShareCode(
        currentCase.id, false, null, null, 'external', isRenew ? { renew: true } : undefined) // no PIN for external
      const priorCode = currentCase.shareCode || currentCase.share_code
      if (isRenew && response?.renewed === true && response.shareCode === priorCode) {
        const expDate = response.expiresAt
          ? new Date(response.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })
          : visitPrepExpiration
        setVisitPrepExpiration(expDate)
        // ★ review r1 MINOR 6: the server HAS renewed the code at this point. A failed save of
        //   the case's copy of the date must not reach the catch below, which handleRegenerateCode
        //   reads as "the renew failed" and tells the patient the code could not be renewed.
        try { await updateAndSaveCase({ shareExpiration: expDate }) }
        catch (saveErr) { console.warn('[VisitPrep] renewed; the case copy of the expiry was not saved:', saveErr?.status || 0) }
        return { renewed: true, expiresAt: response.expiresAt, date: expDate }
      }
      const code = response.shareCode
      const expDate = response.expiresAt 
        ? new Date(response.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' , timeZone: 'America/Los_Angeles'})
        : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' , timeZone: 'America/Los_Angeles'})
      setVisitPrepCode(code)
      setVisitPrepExpiration(expDate)
      
      // Step 2: Generate the clinician hand-off for the share PDF (lean, single
      // Claude call). Returns the merged pre_visit_summary carrying .handoff,
      // which visitPrepPDF.js renders. Non-blocking — PDF degrades gracefully.
      let aiSummary = null
      try {
        aiSummary = await aiAPI.generateHandoffSummary(currentCase.id)
        console.log('[VisitPrep] ✅ Hand-off generated:', !!aiSummary?.handoff?.snapshot)
        
        // Step 2b: Explicitly save PVS to case via update endpoint (backup save)
        // The AI endpoint tries to save internally, but this ensures persistence
        // through the standard case update path with allowed_fields mapping
        if (aiSummary) {
          try {
            const { casesAPI } = await import('../../services/api')
            await casesAPI.update(currentCase.id, { preVisitSummary: aiSummary })
            console.log('[VisitPrep] ✅ PVS explicitly saved to API')
          } catch (saveErr) {
            console.warn('[VisitPrep] PVS explicit save failed (non-critical):', saveErr)
          }
        }
      } catch (aiErr) {
        console.warn('[VisitPrep] AI summary failed, PDF will use raw data:', aiErr)
      }
      
      // Update case with share code + AI summary
      await updateAndSaveCase({
        shareCode: code,
        shareExpiration: expDate,
        isShared: true,
        status: 'shared',
        doctorSource: 'external',
        doctor_source: 'external',
        preVisitSummary: aiSummary,
      })
      
      console.log('[VisitPrep] ✅ Generated share code, present:', !!code)
      // A retry that succeeds makes a 'failed' notice untrue.
      setShareNotice((n) => (n && n.kind === 'failed' ? null : n))
      // Move to disclaimer — PDF is blocked until agreed
      setExtSubState('disclaimer')
      // P11 step 3: the caller learns whether a code was made (handleRegenerateCode's notice).
      return code
    } catch (err) {
      // ★ L10 A7 — a refused renew is not a failure to report here; it is the signal
      //   handleRegenerateCode is waiting for, to fall back to kill-then-mint.
      // ★★ PROD-424 (c), patient push 3a — and so is EVERY other renew failure. A transient 500
      //   or a dropped connection on the renew used to fall through to `return null` below, which
      //   handleRegenerateCode read as "not renewed" and answered by revoking the WORKING code and
      //   minting a new one. A renew failure is now rethrown whatever its status; only the 409
      //   leads to kill-then-mint, anything else keeps the code (handleRegenerateCode).
      if (isRenew) throw err
      console.error('[VisitPrep] Generation failed:', err)
      setGenerateError(err.error || err.message || t('errors2.failedToShareCode'))
      // ★★★ STAY ON 'generating'. It used to go back to 'prep', and Wave 3 deletes that
      //   state — the patient would have got a blank screen, no message and no retry,
      //   because generateError rendered ONLY inside the prep block. The generating
      //   block now owns the error and the Retry.
      autoGenStartedRef.current = false
      return null
    } finally {
      setIsGeneratingPrep(false)
    }
  }

  // v225.15.0 (C-A-174): regenerate the share code.
  // ★★★ L10 A7 (TEST 1.08; Alex L10 D1 = Track A) — RENEW FIRST, NEVER KILL FIRST. This
  //   used to revoke the live code before minting a replacement, so the QR, pass and PDF
  //   already handed to an office died even when nothing was wrong with the code.
  //   `renew:true` is tried first, through the SAME handleGenerateVisitPrep call site
  //   (census: scripts/check-share-code-warning.mjs SCW-6 pins exactly 1 generateShareCode
  //   call inside it and 3 in all of src — a direct second call site here would break
  //   that census on purpose). Only a REFUSED renew (409 share_code_not_renewable —
  //   claimed, sent, or otherwise dead) falls back to the kill-then-mint flow P11 already
  //   warned the patient about when they confirmed. Gate: scripts/check-share-renew.mjs.
  const handleRegenerateCode = async (confirmed) => {
    if (confirmed !== true) { setShareKill('regenerate'); return }
    if (shareKillInFlightRef.current) return
    shareKillInFlightRef.current = true
    setShareKill(null)
    setIsRegenerating(true)
    try {
      const { casesAPI } = await import('../../services/api')
      // ★ Captured BEFORE anything runs, and the local code is NOT cleared on a failed
      //   mint: a null shareCode re-arms the auto-generate effect, which would mint a
      //   second code.
      const oldCode = currentCase.shareCode || currentCase.share_code
      let renewResult
      try {
        renewResult = await handleGenerateVisitPrep({ renew: true })
      } catch (err) {
        if (!(err?.code === 'share_code_not_renewable' || err?.status === 409)) {
          // ★ PROD-424 (c): the renew FAILED (a 500, a timeout, no network) — it was not refused.
          //   Nothing is revoked and nothing is minted. ★ review r1 MINOR 6: what the server
          //   holds for the code is NOT known here (a dropped answer may follow a renew that
          //   happened), so the notice says only that it could not be renewed.
          console.warn('[VisitPrep] renew failed; the current code is kept:', err?.status || 0)
          setShowManageSheet(false)
          setShareNotice({ kind: 'renewFailed', oldCode })
          return
        }
        renewResult = undefined // refused: the kill-then-mint flow below runs instead
      }
      if (renewResult && typeof renewResult === 'object' && renewResult.renewed === true) {
        // The same code and PIN, later expiry. Nothing was killed, nothing was minted.
        setShowManageSheet(false)
        setShareNotice({ kind: 'renewed', date: renewResult.date })
        return
      }
      // Refused (409 share_code_not_renewable): P11's warned kill-then-mint flow, exactly
      // as the patient was told it would work when they confirmed.
      // PROD-364: so is any answer that is not a renewal. The "older API" branch that sat
      // here (a bare minted code, shown as the new code with no revokeShareCode) is gone:
      // the live API answers `renew: true` only with renewed:true or the 409, so it was
      // unreachable, and on an API that did mint it skipped the revoke.
      // ★★ PROD-509 (patient half, after the API's D5 B6 + D6a): a case a physician HOLDS
      //   (doctor_id) or was SENT to (target_doctor_id) must get no new code. The revoke is not
      //   just a code kill: revoke-share (revoke_share_code) clears doctor_id AND
      //   target_doctor_id, sets the case back to ready_to_share and deletes its pending /
      //   scheduled / confirmed / requested appointments. The mint after it then sees a free
      //   case and succeeds — so this button used to WITHDRAW the case from the physician
      //   silently and hand out a new code, under a confirm text that speaks only about the
      //   code. (The mint answers 409 share_code_not_mintable only when the revoke itself was
      //   refused, e.g. a completed case, 400 — then the old code was never killed.)
      //   So the SERVER's case is read before the revoke: the mapped case carries no
      //   doctor_id; the server row is the authority.
      //   GET /cases/{id} answers { case: row } (get_case); the bare row is accepted too.
      //   Held or sent: nothing is revoked, nothing is minted, the case stays with the
      //   physician, and the approved renewFailed notice says the code could not be renewed.
      //   ★ The read itself fails, or answers no row for THIS case: the same stop. Whether the
      //   case is with a physician is then unknown; a kept code costs one retry, a revoke on
      //   a held case withdraws it. Gate: scripts/check-share-code-warning.mjs SCW-9.
      let serverRow = null
      try {
        const fresh = await casesAPI.get(currentCase.id)
        serverRow = fresh && typeof fresh === 'object' ? (fresh.case || fresh) : null
      } catch (readErr) {
        console.warn('[VisitPrep] case read before the revoke failed; the current code is kept:', readErr?.status || 0)
      }
      const rowSeen = !!serverRow && typeof serverRow === 'object' && serverRow.id != null && String(serverRow.id) === String(currentCase.id)
      const withPhysician = rowSeen && !!(serverRow.doctor_id || serverRow.target_doctor_id || serverRow.doctorId || serverRow.targetDoctorId)
      if (!rowSeen || withPhysician) {
        if (withPhysician) console.warn('[VisitPrep] the case is with a physician; no new code can be made, the current code is kept')
        setShowManageSheet(false)
        setShareNotice({ kind: 'renewFailed', oldCode })
        return
      }
      try { await casesAPI.revokeShareCode(currentCase.id) }
      catch (e) { console.warn('[VisitPrep] revoke before regenerate failed (may already be cleared):', e) }
      setShowManageSheet(false)
      const newCode = await handleGenerateVisitPrep({ replacing: true })
      setShareNotice(newCode ? { kind: 'replaced', oldCode, newCode } : { kind: 'failed', oldCode })
    } catch (err) {
      console.error('[VisitPrep] Regenerate code failed:', err)
    } finally {
      setIsRegenerating(false)
      shareKillInFlightRef.current = false
    }
  }

  // v242.207.0 — open the doctor panel. READS ONLY: doctors, favourites, cases.
  const openDoctorPanel = async () => {
    setShowDoctorPanel(true)
    setDoctorPanel({ loading: true, yourDoctors: [], orellaDoctors: [] })
    try {
      const { casesAPI, doctorsAPI, favoritesAPI } = await import('../../services/api')
      // P11 step 4 (D1): the one read-only load, shared with ShareChoiceScreen's Orella card.
      const { yourDoctors, orellaDoctors } = await loadShareDoctorPanel({ casesAPI, doctorsAPI, favoritesAPI, matchDoctorsToCase, getMatchingSpecialties, currentCase })
      setDoctorPanel({ loading: false, yourDoctors, orellaDoctors })
    } catch (err) {
      console.error('[VisitPrep] Doctor panel load failed:', err)
      setDoctorPanel({ loading: false, yourDoctors: [], orellaDoctors: [] })
    }
  }

  // v242.207.0 — a doctor was picked: go straight to that doctor's slot picker.
  // ShareScreen's location.state.selectedDoctor effect opens confirm-doctor.
  const handlePanelDoctorPick = (doctor) => {
    setShowDoctorPanel(false)
    navigate('/case/share', { state: { selectedDoctor: doctor } })
  }

  // Handle cancel external → switch to Orella doctor
  const handleCancelToOrella = async (confirmed) => {
    // ★ Only a case WITH a code is asked: without one there is nothing to kill, and the
    //   switch proceeds exactly as before.
    if (confirmed !== true && (currentCase.shareCode || currentCase.share_code)) { setShareKill('orella'); return }
    if (shareKillInFlightRef.current) return
    shareKillInFlightRef.current = true
    setShareKill(null)
    setIsRevoking(true)
    try {
      const { casesAPI } = await import('../../services/api')
      
      // If we have a share code, revoke it first
      if (currentCase.shareCode || currentCase.share_code) {
        try {
          await casesAPI.revokeShareCode(currentCase.id)
          console.log('[VisitPrep] ✅ Share code revoked')
        } catch (revokeErr) {
          console.warn('[VisitPrep] Revoke API failed (may already be cleared):', revokeErr)
        }
      }
      
      // Switch to orella path on server
      await casesAPI.update(currentCase.id, { 
        doctor_source: 'orella', 
        status: 'ready_to_share',
      })
      
      // Update local state — explicitly clear share data + appointment data
      await updateAndSaveCase({
        doctorSource: 'orella',
        doctor_source: 'orella',
        shareCode: null,
        share_code: null,
        isShared: false,
        status: 'ready_to_share',
        // v199.3: Also clear appointment data from cancelled share
        appointmentRequest: null,
        appointmentStatus: null,
        appointment: null,
        scheduledAppointment: null,
        followUpAppointment: null,
        appointments: [],
      })
      
      console.log('[VisitPrep] ✅ Switched to Orella path')
      setShowCancelModal(false)
      setShowManageSheet(false)
      
      // ★★★ TEST 1.07 — THE ORELLA ROUTE GOES TO THE ORELLA DOCTOR PICKER.
      //   This navigated to /case/share-choice, which is a ONE-OUTCOME screen: its
      //   handleSelect stamped doctor_source='external' + status='submitted' and sent the
      //   patient to /case/detect. So the control the UI badges RECOMMENDED — "Send to a
      //   doctor on orella" — revoked the share code, wrote 'orella' to the server, and was
      //   then immediately overwritten with the OPPOSITE state. The patient ended on the
      //   external-upload screen being asked for the notes of a visit that never happened,
      //   and its own way out ("Share with an Orella Doctor instead") reopened this same
      //   sheet. Every lap destroyed another share code.
      //   ShareScreen opens at step 'find-doctor' — the board-certified doctor picker this
      //   sheet promises. That is where this goes now.
      setTimeout(() => {
        navigate('/case/share') // PROD-654: a push, back returns to Diagnose
      }, 200)
    } catch (err) {
      console.error('[VisitPrep] Cancel to Orella failed:', err)
      setIsRevoking(false)
      setShowCancelModal(false)
      setShowManageSheet(false)
      alert(t('errors2.somethingWentWrong'))
    } finally {
      shareKillInFlightRef.current = false
    }
  }

  // handleRefresh — still needed by the UI refresh buttons below
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try { await loadCases() } catch (err) { console.error('Failed to refresh:', err) }
    setIsRefreshing(false)
  }

  // ★★ PROD-529 — THE SERVER ANSWERS FIRST, AND A REFUSAL WRITES NOTHING. This handler used
  //   to mark the case acknowledged in the local store BEFORE the acknowledge call, swallow
  //   that call's failure, and then write diagnose_acknowledged to the cases table anyway: a
  //   409 (the visit is not signed yet) left the patient's copy and the case row saying
  //   "acknowledged" for a visit the server had refused. Now encountersAPI.acknowledge runs
  //   first and is NOT caught here: any non-2xx (apiFetch throws on !response.ok) aborts the
  //   sequence before a single write, and the patient goes home. Held by
  //   scripts/check-acknowledge-refused.mjs, which executes this handler.
  const handleAcknowledge = async () => {
    setIsAcknowledging(true)
    try {
      await runOverlaySequence(setSubmitPhase, async () => {
        const now = new Date().toISOString()
        // 1. encountersAPI.acknowledge (v120.5.0 — encounter loop sync). A refusal throws here.
        const encounterNum = currentEncounterNumber(currentCase)
        const apiResult = await encountersAPI.acknowledge(currentCase.id, encounterNum)
        console.log('[DiagnoseScreen] Encounter acknowledged:', { success: !!apiResult?.success, newEncounterNumber: apiResult?.newEncounterNumber ?? null }) // PROD-903: no whole answer

        // 2. The local store, only after the server accepted.
        const updates = { diagnoseAcknowledged: true, diagnoseAcknowledgedAt: now }
        if (apiResult?.encounterPhase) updates.encounterPhase = apiResult.encounterPhase
        if (apiResult?.newEncounterNumber) {
          updates.currentEncounter = apiResult.newEncounterNumber
          console.log('[DiagnoseScreen] New encounter created:', apiResult.newEncounterNumber)
        }

        // v135.1.0 PA-A1+A2: Optimistic encounter array update
        const existingEncs = currentCase.encounters || []
        updates.encounters = existingEncs.map(e => {
          const eNum = e.encounter_number || e.encounterNumber
          if (eNum === encounterNum) {
            return { ...e, acknowledged: true, acknowledged_at: now, phase: 'complete' }
          }
          return e
        })

        await updateAndSaveCase(updates)

        // 3. Also persist to cases table for backward compat
        const { casesAPI } = await import('../../services/api')
        try {
          await casesAPI.update(currentCase.id, { 
            diagnose_acknowledged: true,
            diagnose_acknowledged_at: now,
            diagnoseData: { diagnoseAcknowledged: true, diagnoseAcknowledgedAt: now }
          })
          console.log('[DiagnoseScreen] Acknowledgment persisted (columns + JSONB)')
        } catch (err1) {
          console.warn('[DiagnoseScreen] Primary write failed, trying JSONB-only:', err1)
          try {
            await casesAPI.update(currentCase.id, { 
              diagnoseData: { diagnoseAcknowledged: true, diagnoseAcknowledgedAt: now }
            })
          } catch (err2) {
            console.error('[DiagnoseScreen] Both write paths failed:', err2)
          }
        }
        
        // 4. Background refresh to get full server state
        try { await loadCases() } catch (e) { /* non-blocking */ }
      }, () => {
        navigate('/case/home')
      })
    } catch (err) {
      // PROD-529: the acknowledge was refused (or never reached the server); nothing was written.
      console.error('[DiagnoseScreen] Acknowledge refused, nothing written:', err)
      navigate('/case/home')
    }
    setIsAcknowledging(false)
  }

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  if (!currentCase) return <Navigate to="/home" replace />

  // ===== EXTERNAL UPLOAD HANDLERS (A-29) =====
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return
    setIsUploading(true)
    setUploadError(null)

    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB per file

    try {
      const processed = []
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          setUploadError(`"${file.name}" is too large. Maximum 10MB per file.`)
          continue
        }
        // PHI: external notes upload to storage only; base64 is never persisted. Fail closed.
        try {
          const { uploadToStorage } = await import('../../services/storage')
          const storageUrl = await uploadToStorage(currentCase.id, `external-note-${file.name}`, file, file.type)
          if (!storageUrl) {
            setUploadError(`Upload failed for "${file.name}". Please check your connection and try again.`)
            continue
          }
          processed.push({
            id: Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            url: storageUrl,
            data: null,
            storageUrl,
            uploadedAt: new Date().toISOString(),
          })
        } catch (fileErr) {
          setUploadError(`Error uploading "${file.name}": ${fileErr.message || 'Unknown error'}`)
          continue
        }
      }
      if (processed.length > 0) {
        setUploadedFiles(prev => [...prev, ...processed])
      }
    } catch (err) {
      setUploadError(`Error processing files: ${err.message}`)
    } finally {
      setIsUploading(false)
      e.target.value = ''
    }
  }

  const removeUploadedFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const handleSubmitExternalNote = async () => {
    if (uploadedFiles.length === 0) return
    setIsSubmittingUpload(true)
    setAnalysisError(null)
    try {
      const noteData = {
        files: uploadedFiles.map(f => ({
          name: f.name, size: f.size, type: f.type,
          url: f.storageUrl || f.url, storageUrl: f.storageUrl,
          uploadedAt: f.uploadedAt,
        })),
        uploadedAt: new Date().toISOString(),
        status: 'analyzing', // A-30: AI will process immediately
      }
      await updateAndSaveCase({
        externalDoctorNote: noteData,
        external_doctor_note: noteData,
        status: 'submitted',
      })
      // Sync to API
      try {
        const { casesAPI } = await import('../../services/api')
        await casesAPI.update(currentCase.id, {
          external_doctor_note: noteData,
          status: 'submitted',
        })
        console.log('[DiagnoseUpload] ✅ External note synced to API')
      } catch (apiErr) {
        console.warn('[DiagnoseUpload] API sync failed:', apiErr)
      }
      
      // A-30: Trigger AI analysis immediately
      setIsSubmittingUpload(false)
      setIsAnalyzing(true)
      setAnalysisProgress(0)
      
      // Start progress animation
      const progressTimer = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 90) { clearInterval(progressTimer); return 90 }
          return prev + Math.random() * 12
        })
      }, 1500)
      
      try {
        const { aiAPI } = await import('../../services/api')
        console.log('[DiagnoseUpload] 🤖 Starting AI analysis for case:', currentCase.id)
        
        const result = await aiAPI.analyzeExternalNote(currentCase.id)
        clearInterval(progressTimer)
        
        if (result?.success) {
          console.log('[DiagnoseUpload] ✅ AI analysis complete:', result.followUpType)
          setAnalysisProgress(100)
          
          // Update local case with completed status so UI transitions to 'complete'
          await updateAndSaveCase({
            status: 'completed',
            avsContent: result.analysis?.afterVisitSummary || '',
            afterVisitSummary: result.analysis?.afterVisitSummary || '',
            diagnoseData: {
              diagnoses: result.analysis?.diagnoses || [],
              treatments: result.analysis?.treatments || [],
              medications: result.analysis?.medications || [],
              examFindings: result.analysis?.examFindings || '',
              source: 'external_note_ai',
            },
            followUp: {
              // s102 D7: Observation is off (config/featureFlags.js) -> an unread or 'observation' plan is Closed.
              type: (() => { const ty = result.analysis?.followUpPlan?.type; return OBSERVATION_ENABLED ? (ty || 'observation') : (!ty || ty === 'observation' ? 'completed' : ty) })(),
              timeframe: result.analysis?.followUpPlan?.timeframe || '',
              instructions: result.analysis?.followUpPlan?.instructions || '',
              requirements: result.analysis?.followUpPlan?.requirements || {},
              referral: result.analysis?.followUpPlan?.type === 'refer' ? {
                specialty: result.analysis?.followUpPlan?.referralSpecialty || '',
                reason: result.analysis?.followUpPlan?.referralReason || '',
              } : undefined,
              source: 'external_note_ai',
            },
          })
          
          // Refresh from server to get canonical data
          setTimeout(async () => {
            try { await loadCases() } catch (e) { console.warn('Refresh after analysis:', e) }
            setIsAnalyzing(false)
          }, 1200) // Brief delay for the 100% animation to show
          
        } else {
          throw new Error(result?.error || t('diagnose.analysisNoResults'))
        }
      } catch (aiErr) {
        clearInterval(progressTimer)
        console.error('[DiagnoseUpload] AI analysis failed:', aiErr)
        setAnalysisError(aiErr.message || aiErr.error || t('diagnose.analysisFailed'))
        setIsAnalyzing(false)
        // Case is still saved with status 'submitted' — user can retry
      }
    } catch (err) {
      console.error('[DiagnoseUpload] Submit failed:', err)
      setUploadError(t('errors2.failedToSubmit'))
      setIsSubmittingUpload(false)
    }
  }

  // A-30: Retry AI analysis (for cases where analysis failed but files are saved)
  const handleRetryAnalysis = async () => {
    setAnalysisError(null)
    setIsAnalyzing(true)
    setAnalysisProgress(0)
    
    const progressTimer = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 90) { clearInterval(progressTimer); return 90 }
        return prev + Math.random() * 12
      })
    }, 1500)
    
    try {
      const { aiAPI } = await import('../../services/api')
      const result = await aiAPI.analyzeExternalNote(currentCase.id)
      clearInterval(progressTimer)
      
      if (result?.success) {
        setAnalysisProgress(100)
        await updateAndSaveCase({
          status: 'completed',
          avsContent: result.analysis?.afterVisitSummary || '',
          afterVisitSummary: result.analysis?.afterVisitSummary || '',
        })
        setTimeout(async () => {
          try { await loadCases() } catch (e) { /* silent */ }
          setIsAnalyzing(false)
        }, 1200)
      } else {
        throw new Error(result?.error || t('diagnose.analysisNoResults'))
      }
    } catch (err) {
      clearInterval(progressTimer)
      setAnalysisError(err.message || t('diagnose.analysisFailed'))
      setIsAnalyzing(false)
    }
  }

  // ===== STATUS DETECTION =====
  const hasAVS = currentCase.avsContent || currentCase.diagnose?.avsContent || currentCase.afterVisitSummary
  const hasConsultation = hasAVS || currentCase.visitSummary || currentCase.consultNote || currentCase.diagnosis
  const isConsulted = hasConsultation || currentCase.status === 'completed'
  const hasScheduledFollowUpStatus = currentCase.status === 'scheduled' || currentCase.status === 'follow_up' || currentCase.status === 'in_follow_up'
  const isCompleted = currentCase.status === 'completed' || currentCase.status === 'in_follow_up' || currentCase.status === 'in_observation' || isConsulted
  const hasShareCode = !!(currentCase.shareCode || currentCase.share_code)
  const statusIsSharedOrClaimed = currentCase.status === 'shared' || currentCase.status === 'claimed'
  const isShared = currentCase.isShared || statusIsSharedOrClaimed || hasShareCode || hasScheduledFollowUpStatus || isConsulted
  const isPending = isShared && !isConsulted && !hasConsultation

  // External path: check if doctor note was already uploaded
  const hasExternalNote = !!(currentCase.externalDoctorNote || currentCase.external_doctor_note)
  const externalNoteProcessed = hasExternalNote && isConsulted

  // ===== FOLLOW-UP DATA =====
  const rawFollowUpData = currentCase.followUp || currentCase.diagnose?.followUp || {}
  // v223.2.2: Messenger-converted follow-ups carry urgency on the active
  // encounter's care_plan, not on the finalization followUp column. Derive a
  // timeframe (without mutating store state) so the recommendation card shows
  // the doctor-driven window.
  const followUpData = (() => {
    if (rawFollowUpData.timeframe) return rawFollowUpData
    const activeEnc = resolveActiveEncounter(currentCase)
    const urgency = activeEnc?.care_plan?.urgency || activeEnc?.carePlan?.urgency || null
    const timing = urgencyToTiming(urgency)
    return timing ? { ...rawFollowUpData, timeframe: timing.timeframe } : rawFollowUpData
  })()
  const followUpType = followUpData.type || null
  const referralData = followUpData.referral || {}
  const scheduledFollowUp = currentCase.scheduledAppointment?.type === 'follow-up' 
    ? currentCase.scheduledAppointment 
    : (currentCase.appointments?.find(a => a.type === 'follow-up' && a.status === 'scheduled' && !isOutsideAppointment(a)))
  const hasScheduledFollowUp = !!scheduledFollowUp || followUpData.appointmentScheduled
  const followUpCompleted = !!(followUpData.appointmentScheduled && (currentCase.status === 'completed' || currentCase.status === 'finalized'))

  // ===== AVS CONTENT =====
  // PROD-134 gamma: the stored AVS (cases.avs) through the one reader, else today's chain,
  // always a plain string (a finalize envelope or pre-parsed object is unwrapped there).
  const avsContent = readAvsText(currentCase, currentEncounterNumber(currentCase))
  const cleanAvsContent = (content) => {
    if (!content) return ''
    // v123.1: Guard against objects being passed as content
    if (typeof content !== 'string') {
      if (typeof content === 'object' && content.avsContent) return cleanAvsContent(content.avsContent)
      return String(content)
    }
    return content
      .replace(/[━═─┈┉┄┅╌╍│┃┆┇┊┋]/g, '')
      .replace(/^[•\-]\s*$/gm, '')
      .replace(/^-{3,}\s*$/gm, '')
      .replace(/^[=*]{3,}\s*$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .split('\n').map(l => l.trim()).filter(l => l).join('\n')
  }
  const formattedAvs = cleanAvsContent(avsContent)
  const parsedSections = parseAVSIntoSections(formattedAvs, t)

  // ===== DETERMINE STATE =====
  // v138.0.0: STATUS HOLD GATE — prevent backward slip to 'not-shared'
  // If status is a doctor-controlled value, the case has definitively been shared.
  // This catches rehydration race conditions where isShared/shareCode haven't loaded yet.
  const statusGuaranteesShared = ['shared', 'claimed', 'in_progress', 'scheduled', 'completed', 'in_follow_up', 'in_observation'].includes(currentCase.status)
  
  // v230.2.2 — Submit goes DIRECT to the working screen. The instant the user
  // taps "Submit for Orella review", isSubmittingUpload flips true; hold the
  // analyzing screen through upload-save → AI analysis, leaving only on
  // complete/error. This eliminates the two intermediate flashes (the lingering
  // upload screen and the external-pending "Analyze My Notes" card, which
  // confusingly asked the user to tap again after they'd already submitted).
  const state = (isSubmittingUpload || isAnalyzing)
    ? 'external-analyzing'
    : isCompleted 
    ? (isAcknowledged ? 'acknowledged' : 'complete')
    : externalClaimedByOrella
      // External case claimed by Orella doctor — show Orella flow (confirmed/in-progress)
      ? ((currentCase.status === 'claimed' || currentCase.status === 'in_progress') ? 'confirmed' : 'pending')
    : isExternalPath && !hasExternalNote && !analysisError
      ? 'external-upload'
      : isExternalPath && hasExternalNote && !isConsulted && !analysisError
        ? 'external-pending'
        : isExternalPath && analysisError
          ? 'external-error'
          : isPending 
          ? (currentCase.status === 'checked_in' ? 'checked_in'
            : (currentCase.status === 'claimed' || currentCase.status === 'in_progress') ? 'confirmed' : 'pending')
          // v138.0.0: Hold gate — if status guarantees shared but derived flags disagree, show 'pending' not 'not-shared'
          : statusGuaranteesShared
            ? (currentCase.status === 'checked_in' ? 'checked_in'
              : (currentCase.status === 'claimed' || currentCase.status === 'in_progress') ? 'confirmed' : 'pending')
            : 'not-shared'

  // ===== HEADER CONFIG =====
  const caseInfo = `${formatCaseSpecialty(currentCase, t)} › ${currentCase.focusAreaName ? tfa(currentCase.focusAreaName) : ''}`
  // stepNum=4 with totalSteps=3 → all 3 dots filled (complete/acknowledged)
  // stepNum=3 with totalSteps=3 → dots 1,2 filled + dot 3 current light blue
  const dotStep = (state === 'complete' || state === 'acknowledged') ? 4 : 3

  // ===== CHAT MESSAGES =====
  const drName = currentCase.sentToDoctorName || 'your doctor'
  const extChatMessages = {
    generating: t('diagnose.extGeneratingMsg'),
    disclaimer: t('diagnose.extDisclaimerMsg'),
    ready: t('diagnose.extReadyMsg'),
    upload: t('diagnose.extUploadMsg'),
  }
  const drDisplayName = drName.includes('Dr.') ? drName.split(' ').pop() : drName
  const chatMessages = {
    'not-shared': t('diagnose.chatNotShared'),
    'pending': t('diagnose.chatPending'),
    'confirmed': t('diagnose.chatConfirmed', { ...clinicianForms(clinicianFromText(drName), 'short'), doctor: drDisplayName }),
    'checked_in': t('diagnose.chatCheckedIn'),
    'complete': t('diagnose.chatComplete'),
    'acknowledged': t('diagnose.chatAcknowledged'),
    'external-upload': extChatMessages[extSubState] || extChatMessages.generating,
    'external-pending': t('diagnose.chatExternalPending'),
    'external-analyzing': t('diagnose.chatExternalAnalyzing'),
    'external-error': t('diagnose.chatExternalError'),
  }

  console.log('[DiagnoseScreen] State:', state, { status: currentCase.status, isShared, isPending, isCompleted, isAcknowledged })

  return (
    <div className="min-h-full bg-gray-50 flex flex-col">
      <ChatKeyframes />
      <CompactHeader
        stepNum={dotStep}
        totalSteps={3}
        stepLabel={isExternalPath && ['generating', 'disclaimer', 'ready'].includes(extSubState) ? t('diagnose.visitPrepLabel') : 'Diagnose'}
        caseInfo={caseInfo}
        onBack={() => caseBack(navigate)}
        onNotification={() => navigate('/notifications')}
      />
      <OrellaChatBubble
        message={chatMessages[state]}
        animationKey={`diagnose-${state}-${extSubState}-${currentCase?.id}`}
        onComplete={() => setShowContent(true)}
      />

      {/* ===== CONTENT ===== */}
      <div 
        className="flex-1 px-4 pt-2 pb-4 overflow-auto space-y-2"
        style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}
      >

        {/* =============== STATE: Not Yet Shared =============== */}
        {state === 'not-shared' && (
          <div className="bg-gray-50 border-[1.5px] border-gray-200 rounded-2xl p-7 text-center">
            <div className="w-14 h-14 bg-[#E9F8FF] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText size={28} className="text-[#46A8F9]" strokeWidth={1.5} />
            </div>
            <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.shareFirst')}</h3>
            <p className="text-[12px] text-gray-400 leading-relaxed mb-5">
              {t('diagnose.shareFirstBody')}
            </p>
            <button
              onClick={() => navigate('/case/share-hub')}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-bold text-[14px] breathe-blue"
              style={{ backgroundColor: '#46A8F9' }}
            >
              <Share2 size={16} />
              {t('diagnose.goToShare')}
            </button>
          </div>
        )}

        {/* =============== STATE: External Path (A-28/A-29) =============== */}
        {state === 'external-upload' && (
          <>
            {/* Hidden file input (used in upload sub-state) */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* ★★ P11 (D6) — what the last regenerate did. L10 A7 adds 'renewed': the SAME
                code kept working with a later expiry, so this reads as good news, not a
                warning. Otherwise the old code is dead, and either this is the new one or
                no new one could be made. */}
            {shareNotice && (
              <div role="status" className="flex items-start gap-3 rounded-2xl p-3.5 mb-3"
                style={shareNotice.kind === 'failed'
                  ? { background: '#FEF5F5', border: '1.5px solid #F3C9C9' }
                  : shareNotice.kind === 'renewed'
                  ? { background: '#F2FBF6', border: '1.5px solid #BFE8D2' }
                  : { background: '#F3F9FF', border: '1.5px solid #BFE0FD' }}>
                {shareNotice.kind === 'renewed'
                  ? <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#1E9E5A' }} />
                  : <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" style={{ color: shareNotice.kind === 'failed' ? '#C0392B' : '#46A8F9' }} />}
                <p className="flex-1 text-[12.5px] leading-snug" style={{ color: '#15354F' }}>
                  {shareNotice.kind === 'failed'
                    ? t('shareCodeKill.mintFailed')
                    : shareNotice.kind === 'renewFailed'
                    ? t('shareCodeKill.renewFailed')
                    : shareNotice.kind === 'renewed'
                    ? t('shareCodeKill.renewed', { date: shareNotice.date || '' })
                    : t('shareCodeKill.oldCodeDead', { oldCode: shareNotice.oldCode || '', newCode: shareNotice.newCode })}
                </p>
                <button onClick={() => setShareNotice(null)} aria-label={t('common.close')} className="flex-shrink-0 p-0.5" style={{ color: '#5A6B7B' }}>
                  <X size={16} />
                </button>
              </div>
            )}

            {/* ===== SUB-STATE: GENERATING — Navy circle + white O + spinning ring ===== */}
            {extSubState === 'generating' && (
              <>
              {profileMissing && profileMissing.length ? (
                <div className="bg-white rounded-2xl border-[1.5px] border-amber-200 p-6 text-center">
                  <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.beforeShareTitle', 'Before we share your case')}</h3>
                  <p className="text-[13px] text-gray-500 mb-3">{t('diagnose.beforeShareBody', 'Your doctor needs these on file first:')}</p>
                  <ul className="text-[13.5px] text-[#15354F] mb-4 space-y-1">
                    {profileMissing.map((id) => (
                      <li key={id}>{{
                        identification: t('profile.identification'),
                        insurance: t('profile.insurance'),
                        allergies: t('profile.allergies'),
                        medications: t('profile.medications'),
                      }[id]}</li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/onboarding?run=profile')}
                    className="w-full py-3 rounded-2xl font-bold text-white text-[14px] active:scale-[0.97] transition-all"
                    style={{ background: '#46A8F9' }}
                  >
                    {t('diagnose.beforeShareCta', 'Complete my profile')}
                  </button>
                  {/* PROD-1132 Q1 (Alex 2026-10-18): self-pay right here; a saved answer runs the share again
                      (it re-reads missingBeforeShare: still-missing sections keep this card). */}
                  <ContinueWithoutInsurance missing={profileMissing} onDone={() => handleGenerateVisitPrep()} className="mt-2" />
                </div>
              ) : generateError ? (
                <div className="bg-white rounded-2xl border-[1.5px] border-red-200 p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle size={22} className="text-red-500" />
                  </div>
                  <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.generateFailedTitle')}</h3>
                  <p className="text-[12px] text-gray-500 mb-4">{generateError}</p>
                  <button
                    onClick={() => { setGenerateError(null); handleGenerateVisitPrep() }}
                    disabled={isGeneratingPrep}
                    className="w-full py-3 rounded-2xl font-bold text-white text-[14px] active:scale-[0.97] transition-all"
                    style={{ background: '#15354F' }}
                  >
                    {t('diagnose.generateRetry')}
                  </button>
                </div>
              ) : (
              <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 p-7 text-center">
                {/* s123: the Case Report builds under the app's one spinner (board G) */}
                <OrellaSpinner size={96} className="mx-auto mb-5" />
                <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.generatingVisitPrep')}</h3>
                <p className="text-[12px] text-gray-400 mb-4">{t('diagnose.creatingShareCode')}</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {[t('diagnose.chipProfile'), t('diagnose.chipSymptoms'), t('diagnose.chipPhotos'), t('diagnose.chipAiSummary'), t('diagnose.chipPdf'), t('diagnose.chipShareCode')].map((label, i) => (
                    <span
                      key={label}
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
                      style={{
                        background: i < 1 ? '#DCFCE7' : i === 1 ? '#E9F8FF' : '#F3F4F6',
                        color: i < 1 ? '#16A34A' : i === 1 ? '#46A8F9' : '#9CA3AF',
                      }}
                    >
                      {i < 1 && <Check size={10} strokeWidth={3} />}
                      {i === 1 && <div className="w-2 h-2 rounded-full bg-[#46A8F9] animate-pulse" />}
                      {label}
                    </span>
                  ))}
                </div>
                {/* Animation keyframes */}
                <style>{`
                `}</style>
              </div>
              )}

              {/* What's included — moved off the deleted prep screen. It belongs HERE,
                  where it describes what is being built rather than gating it. */}
              <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 p-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2.5">{t('diagnose.docWillInclude')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: ClipboardList, label: t('diagnose.healthProfile') },
                    { icon: FileText, label: t('diagnose.symptomDetails') },
                    { icon: Eye, label: t('diagnose.photosScans') },
                    { icon: Lock, label: t('diagnose.digitalShareCode') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-xl" style={{ background: '#E9F8FF60' }}>
                      <item.icon size={14} className="text-[#46A8F9]" />
                      <span className="text-[11px] font-semibold text-[#15354F]">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              </>
            )}

            {/* ===== SUB-STATE: DISCLAIMER — Agreement gate ===== */}
            {extSubState === 'disclaimer' && (
              <VisitPrepDisclaimer
                onAgree={async () => {
                  // Persist agreement to case state — blocks PDF until agreed
                  try {
                    await updateAndSaveCase({ visitPrepAgreed: true, visitPrepAgreedAt: new Date().toISOString() })
                    const { casesAPI } = await import('../../services/api')
                    await casesAPI.update(currentCase.id, { visit_prep_agreed: true, visit_prep_agreed_at: new Date().toISOString() })
                  } catch (err) { console.warn('[VisitPrep] Agreement sync failed:', err) }
                  if (viewAfterAgreeRef.current) { viewAfterAgreeRef.current = false; setExtSubState('upload'); setShowReportViewer(true); return }
                  setExtSubState('ready')
                }}
                onCancel={() => {
                  if (viewAfterAgreeRef.current) { viewAfterAgreeRef.current = false; setExtSubState('upload'); return }
                  navigate('/case/home')
                }}
              />
            )}

            {/* ===== SUB-STATE: READY — Code + QR + PDF + Share ===== */}
            {extSubState === 'ready' && (
              <>
                <VisitPrepCodeDisplay
                  shareCode={visitPrepCode}
                  expirationDate={visitPrepExpiration}
                />

                {/* Doctor + appointment — moved off the deleted prep screen to HERE,
                    beside the code the patient is about to send, which is also where
                    Wave 4's send will want them. */}
                <ExternalDoctorLookup
                  savedDoctor={savedDoctor}
                  onSave={handleSaveDoctor}
                  specialty={currentCase.specialtyName}
                  sendVariant
                />

                <AppointmentEntry
                  savedDoctor={savedDoctor}
                  currentCase={currentCase}
                  onAppointmentSaved={(apt) => console.log('[DiagnoseScreen] Appointment saved, id:', apt?.id ?? null)}
                />

                {/* ★ Case Home — his layout call, 2026-09-11: the exit belongs AFTER the
                    doctor and the appointment, not between the code and them. Keyframes
                    come from VisitPrepCodeDisplay, which renders directly above. */}
                <button
                  onClick={() => navigate('/case/home')}
                  className="relative overflow-hidden w-full py-3.5 rounded-2xl font-bold text-[14px] text-white flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
                  style={{ background: '#46A8F9', animation: 'vpPulse 3s ease-in-out infinite' }}
                >
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'vpShimmer 3s ease-in-out infinite',
                    }}
                  />
                  <Home size={16} style={{ position: 'relative', zIndex: 1 }} />
                  <span style={{ position: 'relative', zIndex: 1 }}>{t('visitPrep.caseHome')}</span>
                </button>

                {/* Cancel share + pivot to Orella doctor */}
                <div className="text-center pt-1">
                  <button
                    className="text-[11px] font-semibold text-[#46A8F9] mt-0.5"
                    onClick={openDoctorPanel}
                  >
                    {t('diagnose.cancelShareSwitchOrella')}
                  </button>
                </div>
              </>
            )}

            {/* ===== SUB-STATE: UPLOAD — Post-visit upload ===== */}
            {extSubState === 'upload' && (
              <>
                {/* Collapsed visit prep card (if code was generated) — v229.2.0:
                    on-brand document treatment (white card, ice-blue icon tile,
                    navy eyebrow, mono code matching VisitPrepCodeDisplay, solid
                    blue View). Green dropped — it's reserved for success/Deliver. */}
                {visitPrepCode && (
                  <div className="rounded-2xl border-[1.5px] border-[#D8E6F0] bg-white p-3.5 flex items-center gap-3" style={{ boxShadow: '0 2px 10px rgba(21,53,79,0.04)' }}>
                    <div className="w-11 h-11 rounded-xl bg-[#E9F8FF] border border-[#A7D9FC] flex items-center justify-center flex-shrink-0">
                      <FileText size={20} className="text-[#46A8F9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10.5px] font-extrabold uppercase tracking-[0.09em] text-[#15354F]">{t('diagnose.visitPrepDoc')}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                        <span className="text-[12px] font-mono font-semibold text-[#15354F] tracking-[0.02em]">{visitPrepCode}</span>
                        {savedDoctor?.name ? (
                          <>
                            <span className="text-[11px] text-[#5F7A8E]">·</span>
                            <span className="text-[11.5px] text-[#5F7A8E] font-medium truncate">{savedDoctor.name}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // s99 D477 (Build 61 TestFlight): View opens the shared report in the
                        //   in-app viewer, not the old code screen. The agreement comes first,
                        //   once per case.
                        if (currentCase?.visitPrepAgreed || currentCase?.visit_prep_agreed) setShowReportViewer(true)
                        else { viewAfterAgreeRef.current = true; setExtSubState('disclaimer') }
                      }}
                      data-cta="diagnose-view-report"
                      className="inline-flex items-center gap-1.5 text-[12px] font-bold text-white bg-[#46A8F9] px-3.5 py-2 rounded-full active:scale-95 transition-all flex-shrink-0"
                    >
                      <Eye size={13} />
                      {t('diagnose.view', 'View')}
                    </button>
                  </div>
                )}

                {/* Appointment Entry — available in upload state too */}
                <AppointmentEntry
                  savedDoctor={savedDoctor}
                  currentCase={currentCase}
                  onAppointmentSaved={(apt) => console.log('[DiagnoseScreen] Appointment saved, id:', apt?.id ?? null)}
                />

                {/* Upload Zone */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full rounded-2xl p-6 text-center border-2 border-dashed border-gray-300 bg-white active:bg-gray-50 transition-colors"
                >
                  <div className="relative mx-auto mb-3 w-14 h-14">
                    <div className="absolute inset-[-6px] rounded-2xl border-2 border-[#46A8F9]/15" style={{ animation: 'ringPulse 2.5s ease-in-out infinite' }} />
                    <div className="w-14 h-14 bg-[#E9F8FF] rounded-2xl flex items-center justify-center">
                      {isUploading ? (
                        <OrellaSpinner size={20} />
                      ) : (
                        <Upload size={26} className="text-[#46A8F9]" strokeWidth={1.5} />
                      )}
                    </div>
                  </div>
                  <p className="text-[14px] font-bold text-[#15354F]">
                    {isUploading ? t('diagnose.processing') : t('diagnose.tapToUploadNotes')}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">{t('diagnose.pdfPhotosMax')}</p>
                </button>

                {/* Upload Error */}
                {uploadError && (
                  <div className="rounded-xl p-3 bg-red-50 border border-red-200 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-red-600">{uploadError}</p>
                  </div>
                )}

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-[#15354F]">
                      {t('diagnose.uploadedFiles')}
                    </span>
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border-[1.5px] border-gray-200">
                        <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                          <FileText size={18} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-gray-800 truncate">{file.name}</p>
                          <p className="text-[11px] text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                        <button onClick={() => removeUploadedFile(file.id)} className="p-1.5 text-red-400 active:text-red-600">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-2 text-gray-400 text-[13px] active:bg-gray-50"
                    >
                      <Plus size={16} /><span>{t('diagnose.addAnotherFile')}</span>
                    </button>
                  </div>
                )}

                {/* What to upload guidance */}
                <div className="bg-white rounded-2xl p-4 border-[1.5px] border-gray-200">
                  <p className="text-[12px] font-bold text-[#15354F] mb-2.5">{t('diagnose.whatToUpload')}</p>
                  <div className="space-y-3">
                    {[
                      { Icon: ClipboardList, label: t('diagnose.visitSummaryDoctor'), desc: t('diagnose.whatYourDoctorFoundAnd') },
                      { Icon: Pill, label: t('diagnose.prescriptions'), desc: t('diagnose.anyNewMedicationsPrescribed') },
                      { Icon: Stethoscope, label: t('diagnose.testResultsOrLabWork'), desc: t('diagnose.bloodWorkImagingHearingTests') },
                      { Icon: FileText, label: t('diagnose.referralLetters'), desc: t('diagnose.ifReferredToASpecialist') },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#E9F8FF] flex items-center justify-center flex-shrink-0">
                          <item.Icon size={16} className="text-[#46A8F9]" />
                        </div>
                        <div>
                          <p className="text-[12px] font-semibold text-gray-700">{item.label}</p>
                          <p className="text-[11px] text-gray-400">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>


                {/* Footer actions */}
                <div className="pt-2 pb-2 space-y-2">
                  <button
                    onClick={handleSubmitExternalNote}
                    disabled={uploadedFiles.length === 0 || isSubmittingUpload}
                    className="w-full py-3.5 rounded-full font-bold text-[15px] flex items-center justify-center gap-2 transition-all"
                    style={{
                      backgroundColor: uploadedFiles.length > 0 ? '#15354F' : '#F3F4F6',
                      color: uploadedFiles.length > 0 ? 'white' : '#9CA3AF',
                    }}
                  >
                    {isSubmittingUpload ? (
                      <OrellaSpinner size={20} tone="inherit" />
                    ) : uploadedFiles.length > 0 ? (
                      <>
                        <Check size={18} strokeWidth={2.5} />
                        {t('diagnose.submitForReview')}
                      </>
                    ) : (
                      t('diagnose.uploadFilesToContinue')
                    )}
                  </button>
                  {/* ★ s99 D476 (Dr. Rami 2026-10-10, "Orella Switch Doctor"): the buried "Changed your
                      mind? Share with an Orella Doctor instead" link became this pill. It withdraws
                      the case (one live share at a time) and opens the share hub, which makes a new
                      code; from there any doctor, on Orella or in the directory. */}
                  <button
                    onClick={() => setShowSwitchSheet(true)}
                    data-cta="diagnose-switch-doctor"
                    className="w-full py-3 rounded-full font-bold text-[14px] flex items-center justify-center gap-2 bg-white active:scale-[0.98]"
                    style={{ border: '1.5px solid #15354F', color: '#15354F' }}
                  >
                    <Repeat size={15} />
                    {t('diagnose.cancelShareAnother', 'Cancel & share with another doctor')}
                  </button>
                  <button
                    onClick={() => navigate('/case/home', { replace: true })}
                    className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98]"
                  >
                    <Home size={15} />
                    {t('visitPrep.caseHome', 'Case Home')}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* =============== STATE: External Note Pending AI Processing =============== */}
        {state === 'external-pending' && (
          <div className="bg-white border-[1.5px] border-gray-200 rounded-2xl p-7 text-center">
            <div className="relative mx-auto mb-4 w-14 h-14">
              <div className="absolute inset-[-6px] rounded-2xl border-2 border-[#15354F]/20 animate-[ringPulse_2s_ease-in-out_infinite]" />
              <div className="w-14 h-14 bg-[#E9F8FF] rounded-2xl flex items-center justify-center">
                <Clock size={28} className="text-[#15354F]" strokeWidth={1.5} />
              </div>
            </div>
            <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.notesUploaded')}</h3>
            <p className="text-[12px] text-gray-400 leading-relaxed mb-4">
              {t('diagnose.notesUploadedBody')}
            </p>
            <button
              onClick={handleRetryAnalysis}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-[14px] active:scale-[0.97] transition-all"
              style={{ backgroundColor: '#15354F', boxShadow: '0 4px 14px rgba(21,53,79,0.25)' }}
            >
              <Search size={16} />
              {t('diagnose.analyzeMyNotes')}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-[1.5px] border-gray-200 text-[13px] font-semibold text-gray-500 active:bg-gray-50"
            >
              {isRefreshing ? <OrellaSpinner size={16} tone="inherit" /> : <RefreshCw size={14} />}
              {isRefreshing ? t('diagnose.checking') : t('diagnose.checkForUpdates')}
            </button>
          </div>
        )}

        {/* =============== STATE: AI Analyzing External Note (A-30) =============== */}
        {state === 'external-analyzing' && (
          <div className="bg-white border-[1.5px] border-gray-200 rounded-2xl p-7 text-center">
            {/* s123: the Case Report builds under the app's one spinner (board G) */}
            <OrellaSpinner size={96} className="mx-auto mb-5" />
            <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.analyzingNotes')}</h3>
            <p className="text-[12px] text-gray-400 mb-4">{t('diagnose.extractingDiagnoses')}</p>
            
            {/* Progress bar */}
            <div className="w-full max-w-[240px] mx-auto bg-gray-100 rounded-full h-2 mb-3 overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{ 
                  width: `${Math.min(analysisProgress, 100)}%`,
                  background: analysisProgress >= 100 
                    ? 'linear-gradient(90deg, #16A34A, #22C55E)' 
                    : 'linear-gradient(90deg, #15354F, #46A8F9)',
                }}
              />
            </div>
            
            {/* Step indicators */}
            <div className="flex flex-wrap gap-1.5 justify-center">
              {[
                { label: t('diagnose.readingDocuments'), threshold: 10 },
                { label: t('diagnose.findingDiagnoses'), threshold: 30 },
                { label: t('diagnose.extractingMedications'), threshold: 55 },
                { label: t('diagnose.buildingCarePlan'), threshold: 75 },
                { label: t('diagnose.creatingSummary'), threshold: 90 },
              ].map((step, i) => (
                <span
                  key={step.label}
                  className="text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
                  style={{
                    background: analysisProgress >= step.threshold ? '#DCFCE7' : analysisProgress >= step.threshold - 15 ? '#E9F8FF' : '#F3F4F6',
                    color: analysisProgress >= step.threshold ? '#16A34A' : analysisProgress >= step.threshold - 15 ? '#46A8F9' : '#9CA3AF',
                  }}
                >
                  {analysisProgress >= step.threshold && <Check size={10} strokeWidth={3} />}
                  {analysisProgress >= step.threshold - 15 && analysisProgress < step.threshold && (
                    <div className="w-2 h-2 rounded-full bg-[#46A8F9] animate-pulse" />
                  )}
                  {step.label}
                </span>
              ))}
            </div>
            
            <style>{`
            `}</style>
          </div>
        )}

        {/* =============== STATE: External Note Analysis Error =============== */}
        {state === 'external-error' && (
          <div className="bg-white border-[1.5px] border-red-200 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={28} className="text-red-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-[15px] font-bold text-[#15354F] mb-1">{t('diagnose.analysisIssue')}</h3>
            <p className="text-[12px] text-gray-400 leading-relaxed mb-2">
              {analysisError || t('diagnose.somethingWentWrong')}
            </p>
            <p className="text-[11px] text-gray-400 mb-4">
              {t('diagnose.analysisIssueBody')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRetryAnalysis}
                className="w-full py-3 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2"
                style={{ backgroundColor: '#15354F' }}
              >
                <RefreshCw size={16} />
                {t('diagnose.tryAgain')}
              </button>
              <button
                onClick={() => { 
                  setAnalysisError(null)
                  // Clear existing notes so state machine goes back to 'external-upload'
                  updateAndSaveCase({
                    externalDoctorNote: null,
                    external_doctor_note: null,
                    status: 'shared',
                  })
                  setUploadedFiles([])
                  setExtSubState('upload')
                }}
                className="w-full py-2.5 text-[13px] font-semibold text-[#46A8F9]"
              >
                {t('diagnose.uploadDifferentFiles')}
              </button>
              <button
                onClick={() => navigate('/case/home', { replace: true })}
                className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-1.5"
              >
                {t('diagnose.backToCaseHome')}
              </button>
            </div>
          </div>
        )}

        {/* ═══ Share Code Reference (external cases that were claimed by Orella MD) ═══ */}
        {externalClaimedByOrella && displayShareCode && (state === 'confirmed' || state === 'pending' || state === 'checked_in') && (
          <div className="bg-[#E9F8FF] border border-[#A7D9FC] rounded-2xl px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center">
                  <FileText size={14} className="text-[#46A8F9]" />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-[#15354F]">{t('diagnose.doctorAcceptedOrella')}</p>
                  <p className="text-[10px] text-[#46A8F9]">{t('diagnose.caseManagedPlatform')}</p>
                </div>
              </div>
              <span className="font-mono text-[12px] font-bold text-[#15354F] bg-white px-2.5 py-1 rounded-lg border border-[#A7D9FC]">{displayShareCode}</span>
            </div>
          </div>
        )}

        {/* =============== STATE: Pending / Confirmed Review =============== */}
        {(state === 'pending' || state === 'confirmed' || state === 'checked_in') && (() => {
          const doc = currentCase.doctor || currentCase.sentToDoctorData
          const docName = currentCase.sentToDoctorName || (doc ? clinicianName(withCaseClinician(doc, currentCase)) : 'Your Doctor')
          const docInitials = ((doc?.first_name?.[0] || doc?.firstName?.[0] || docName?.[4] || '') + (doc?.last_name?.[0] || doc?.lastName?.[0] || '')).toUpperCase() || '?'
          const docSpecialty = doc?.specialty || ''
          const apptReq = currentCase.appointmentRequest
          const isApptConfirmed = apptReq?.status === 'confirmed'
          const apptDate = isApptConfirmed ? (apptReq.confirmedDate || apptReq.requestedDate) : apptReq?.requestedDate
          const apptTime = isApptConfirmed ? (apptReq.confirmedTime || apptReq.requestedTime) : apptReq?.requestedTime
          const apptType = (isApptConfirmed ? (apptReq.confirmedType || apptReq.appointmentType) : apptReq?.appointmentType) || 'in_person'
          // MEET PA2: the initial appointment ROW carries the link, id, provider and decision;
          //   appointmentRequest's link fields are only a fallback for the link.
          const joinTarget = videoJoinTarget(initialAppointmentRow(currentCase), apptReq)

          return (
          <>
            {/* v225.24.0: Shared pending-consult view (doctor + appointment +
                no-data fallback) — identical render to the follow-up Diagnose
                pending state so the two flows match. The check-in button, QR/
                Wallet pass, upload, and refresh below remain initial-specific. */}
            <PendingConsultCard
              docName={doc ? docName : ''}
              docSpecialty={docSpecialty}
              docPracticeName={doc?.practice_name || ''}
              docPhoto={doc?.profile_photo || null}
              docInitials={docInitials}
              statusLabel={state === 'checked_in' ? t('diagnose.checkedIn') : state === 'confirmed' ? t('diagnose.accepted') : t('diagnose.reviewing')}
              statusTone={state === 'checked_in' ? 'checkin' : 'blue'}
              // PROD-1137: a visit the physician cancelled is not "requested": the card names the
              //   cancel and the cancelled slot; its reschedule opens "Choose a new time" on Case Home.
              apptTitle={physicianCancelOfRequest(apptReq)
                ? t('caseHome.cancelledByPhysician', { ...clinicianForms(doc ? withCaseClinician(doc, currentCase) : clinicianFromText(docName)), doctorName: String(docName || '').replace(/^Dr\.?\s+/i, '') })
                : isApptConfirmed ? t('diagnose.appointmentConfirmed') : t('diagnose.appointmentRequested')}
              apptDateISO={physicianCancelOfRequest(apptReq)?.date || (apptReq && apptDate ? apptDate : '')}
              apptTime={physicianCancelOfRequest(apptReq)?.time || apptTime}
              apptType={apptType}
              isApptConfirmed={isApptConfirmed}
              joinTarget={joinTarget}
              onJoinCheckIn={() => navigate(joinCheckInPath(currentCase?.id, joinTarget?.id))}
              onReschedule={() => navigate('/case/home', { state: { openReschedule: true } })}
              fallbackTitle={state === 'confirmed' ? t('diagnose.doctorReviewing') : t('diagnose.awaitingDoctorReview')}
              fallbackBody={t('diagnose.pendingFallbackBody')}
              eligibility={checkInProfile?.insurance?.last_eligibility || null}
              onViewProfile={(doc?.id || currentCase.doctorId || currentCase.doctor_id)
                ? () => navigate(`/doctor/${doc?.id || currentCase.doctorId || currentCase.doctor_id}`)
                : undefined}
              t={t}
            />

            {/* v177.2.0: Check In button — requires:
                1. Appointment confirmed by doctor
                2. Case claimed/in_progress (doctor has accepted)
                3. Within 24 hours of appointment time
                4. Not already checked in */}
            {(() => {
              if (!isApptConfirmed) return null
              if (currentCase.status === 'checked_in') return null
              if (currentCase.status !== 'claimed' && currentCase.status !== 'in_progress' && currentCase.status !== 'scheduled') return null
              // ★ PROD-1007: while a reschedule request is held its NEW time opens no check-in; the old
              //   kept old slot (confirmedCheckInSlot, status_history held entry) does, and with none there is nothing to check in to.
              const heldSlot = hasPendingReschedule(currentCase) ? confirmedCheckInSlot(currentCase) : null
              if (hasPendingReschedule(currentCase) && !heldSlot) return null
              // 24-hour gate: only show if appointment is within the next 24 hours
              const apptDateStr = heldSlot ? heldSlot.date : (apptReq?.confirmedDate || apptReq?.requestedDate)
              const apptTimeStr = heldSlot ? heldSlot.time : (apptReq?.confirmedTime || apptReq?.requestedTime)
              if (apptDateStr && apptTimeStr) {
                // ★ PROD-1368: the slot is the practice's wall clock (ORELLA_TZ); wallTimeToInstant makes it the
                //   true instant, so the window opens at the same moment on every device. null (unparseable) does not block.
                const apptInstant = wallTimeToInstant(apptDateStr, apptTimeStr)
                if (apptInstant) {
                  const hoursUntil = (apptInstant.getTime() - Date.now()) / (1000 * 60 * 60)
                  if (!isWithinCheckInWindow(hoursUntil)) return null // PROD-1369: the shared boundary (same as Visits + appointmentWindow; closed at exactly +2 h)
                }
              }
              // s114 (canvas V1): a video visit checks in on the app with no code to show
              const videoCheckIn = isVirtualVisitType(heldSlot?.type || apptReq?.confirmedType || apptReq?.appointmentType)
              return (
                <>
                <button
                  onClick={() => setShowCheckIn(true)}
                  className="w-full py-3.5 rounded-full text-white text-[15px] font-bold flex items-center justify-center gap-2 active:opacity-90 transition-all breathe-green"
                  style={{ backgroundColor: '#46A8F9' }}
                >
                  <Check size={18} strokeWidth={2.5} />
                  {videoCheckIn ? t('videoSignOn.checkInButton') : (t('checkIn.checkInForVisit') || 'Check In for Your Visit')}
                </button>
                {videoCheckIn && <p data-video-checkin-note className="text-[12.5px] text-center text-[#5F7A8E] mt-1.5">{t('videoSignOn.checkInNote')}</p>}
                </>
              )
            })()}

            {/* v181.8.3: Checked-in status badge — tappable to show QR code */}
            {currentCase.status === 'checked_in' && (
              <>
                {/* s114 (canvas V5): a checked-in VIDEO visit opens its Join screen (sign-on first), never a QR pass */}
                <button onClick={() => (isVirtualVisitType(apptReq?.confirmedType || apptReq?.appointmentType) ? setShowCheckIn(true) : setShowQRModal(true))} className="flex items-center justify-center w-full">
                  <div className="flex items-center gap-2 bg-[#E9F8FF] border-[1.5px] border-[#A7D9FC] rounded-full px-5 py-2.5 active:bg-[#D5F0FF] transition-colors">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#46A8F9' }} />
                    <span className="text-[13px] font-bold text-[#15354F]">{t('diagnose.checkedIn')}</span>
                    <ChevronRight size={14} className="text-[#94A3B8]" />
                  </div>
                </button>

                {/* v225.0.x: the designed Wallet-style Visit Pass (same pass
                    used at check-in confirm + Visits), replacing the ad-hoc
                    QR mini-modal so the patient sees one consistent pass. */}
                {showQRModal && (() => {
                  const ident = checkInProfile?.identification || {}
                  const passName = `${ident.firstName || ''} ${ident.lastName || ''}`.trim() || 'Patient'
                  const provName = clinicianName(withCaseClinician(doc || {}, currentCase))
                  const addr = doc?.practice_address ? `${formatAddress(doc.practice_address)}${doc.practice_city ? `, ${doc.practice_city}` : ''}` : ''
                  const dRaw = apptReq?.confirmedDate || apptReq?.requestedDate
                  let dLabel = ''
                  if (dRaw) { try { const [yy, mm, dd] = dRaw.split('-').map(Number); dLabel = new Date(yy, mm - 1, dd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) } catch { dLabel = dRaw } }
                  const tRaw = apptReq?.confirmedTime || apptReq?.requestedTime
                  let tLabel = ''
                  if (tRaw) { const [hh, mn] = tRaw.split(':').map(Number); const ap = hh >= 12 ? 'PM' : 'AM'; const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh; tLabel = `${h12}:${String(mn || 0).padStart(2, '0')} ${ap}` }
                  return (
                    <WalletPassModal
                      isOpen={showQRModal}
                      onClose={() => setShowQRModal(false)}
                      caseId={currentCase?.id}
                      patientName={passName}
                      providerName={provName}
                      practiceName={doc?.practice_name || ''}
                      addressLine1={addr}
                      dateLabel={dLabel}
                      timeLabel={tLabel}
                      token={currentCase.checkIn?.token}
                    />
                  )
                })()}
              </>
            )}

            {/* Upload additional documents — links to Detect */}
            <button
              onClick={() => navigate('/case/detect')}
              className="w-full flex items-center gap-2 px-3.5 py-3 bg-gray-50 border-[1.5px] border-dashed border-gray-200 rounded-xl text-[12px] font-semibold text-[#46A8F9] hover:bg-[#E9F8FF] hover:border-[#A7D9FC] transition-all"
            >
              <Upload size={15} />
              {t('diagnose.uploadAdditionalDocs')}
              <ChevronRight size={14} className="text-gray-300 ml-auto" />
            </button>

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold text-gray-400"
            >
              {isRefreshing ? <OrellaSpinner size={16} tone="inherit" /> : <RefreshCw size={13} />}
              {t('diagnose.checkForUpdates')}
            </button>
          </>
          )
        })()}

        {/* =============== STATE: Complete (Unacknowledged) =============== */}
        {state === 'complete' && (
          <>
            {/* AVS Cards — Guided walkthrough on first view */}
            {parsedSections ? (
              <AVSReview
                avsText={formattedAvs}
                caseId={currentCase?.id}
                gated={true}
                onAllOpened={() => setWalkthroughFinished(true)}
                t={t}
              />
            ) : formattedAvs ? (
              <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5">
                <div className="text-[14px] text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                  {safeText(formattedAvs)}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5 text-center">
                <p className="text-[14px] text-gray-400">{t('diagnose.consultSummaryAvailable')}</p>
              </div>
            )}

            {/* Follow-up care summary — only show after walkthrough completes (v212.0) */}
            {(!parsedSections || walkthroughFinished) && (
              <>
                {(currentCase.followUpDiagnose || currentCase.diagnose?.followUpConsultNote) && (
                  <FollowUpUpdateCard data={followUpData} caseData={currentCase} />
                )}

                {currentCase.followUpUpdates && currentCase.followUpUpdates.length > 0 && (
                  <FollowUpUpdatesList updates={currentCase.followUpUpdates} />
                )}

                {currentCase.diagnose?.followUpNote && !currentCase.followUpUpdates && !currentCase.followUpDiagnose && (
                  <FollowUpUpdateCard data={followUpData} caseData={currentCase} />
                )}

                {!followUpCompleted && followUpType && (
                  <FollowUpTypeCard type={followUpType} data={followUpData} referralData={referralData} />
                )}
              </>
            )}
          </>
        )}

        {/* =============== STATE: Acknowledged (Preserved / Locked) =============== */}
        {state === 'acknowledged' && (
          <>
            {/* Preserved state banner */}
            <div className="bg-green-50 border border-green-200 rounded-[12px] p-3">
              <div className="flex items-center gap-[8px]">
                <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Lock size={14} className="text-green-600" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold text-green-700">{t('diagnose.carePlanAcknowledged')}</p>
                  <p className="text-[10.5px] text-green-500 mt-0.5">
                    {currentCase.diagnoseAcknowledgedAt 
                      ? t('diagnose.confirmedAt', { date: formatDateTime(currentCase.diagnoseAcknowledgedAt) })
                      : 'Confirmed'}
                  </p>
                </div>
                <Check size={18} className="text-green-500 flex-shrink-0" strokeWidth={2.5} />
              </div>
            </div>

            {/* AVS Cards (preserved read-only state — slightly muted) */}
            <div className="opacity-85">
              {parsedSections ? (
                <AVSChecklist sections={parsedSections} gated={false} t={t} />
              ) : formattedAvs ? (
                <div className="bg-white rounded-[14px] border-[1.5px] border-gray-200 p-3.5">
                  <div className="text-[14px] text-gray-600 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                    {safeText(formattedAvs)}
                  </div>
                </div>
              ) : null}

              {/* Follow-up care data */}
              {(currentCase.followUpDiagnose || currentCase.diagnose?.followUpConsultNote || currentCase.diagnose?.followUpNote) && (
                <FollowUpUpdateCard data={followUpData} caseData={currentCase} />
              )}
              {currentCase.followUpUpdates && currentCase.followUpUpdates.length > 0 && (
                <FollowUpUpdatesList updates={currentCase.followUpUpdates} />
              )}

              {/* Follow-up type card */}
              {!followUpCompleted && followUpType && (
                <FollowUpTypeCard type={followUpType} data={followUpData} referralData={referralData} />
              )}
            </div>
          </>
        )}
      </div>

      {/* ===== FOOTER ===== */}
      <div className="p-4 pb-5 flex-shrink-0 bg-white border-t border-gray-100" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
        
        {/* Complete (unacknowledged): green "I've Reviewed This" button.
            v217.1.4: Gated on walkthroughFinished. The CTA is disabled until
            the user has either continued through every section (last "Done")
            or tapped "Skip to End". When there are no parsed sections (flat
            AVS or empty state), there's no walkthrough to complete, so the
            CTA is active immediately. */}
        {state === 'complete' && (() => {
          const reviewReady = !parsedSections || walkthroughFinished
          return (
            <button
              onClick={reviewReady ? handleAcknowledge : undefined}
              disabled={isAcknowledging || !reviewReady}
              className={`w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 ${
                !isAcknowledging && reviewReady ? 'breathe-green' : ''
              } ${reviewReady ? '' : 'opacity-50 cursor-not-allowed'} ${
                isAcknowledging ? 'opacity-50' : ''
              }`}
              style={{ backgroundColor: '#16A34A' }}
              aria-disabled={!reviewReady}
            >
              {isAcknowledging ? t('common.saving') : (
                <><Check size={18} strokeWidth={2.5} /> {t('diagnose.iveReviewedThis')}</>
              )}
            </button>
          )
        })()}

        {/* Acknowledged: locked state with "Go to Deliver" CTA */}
        {state === 'acknowledged' && (
          <>
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Lock size={11} className="text-gray-300" />
              <span className="text-[10.5px] text-gray-300 font-medium">{t('diagnose.sectionLocked')}</span>
            </div>
            {/* v234.15.2: MD-agnostic case — Deliver is locked (no Orella MD), so there is no
                Deliver to route to. Drop "Go to Deliver" and make "Back to Case Home" primary. */}
            {!deliverNeedsOrellaMD && (
              <button
                onClick={() => navigate('/case/deliver')}
                className="w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 breathe-navy"
                style={{ backgroundColor: '#15354F' }}
              >
                {t('diagnose.goToDeliver')}
              </button>
            )}
            <button
              onClick={() => navigate('/case/home')}
              className={deliverNeedsOrellaMD
                ? "w-full py-3.5 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 breathe-navy"
                : "w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold mt-2 flex items-center justify-center gap-1.5"}
              style={deliverNeedsOrellaMD ? { backgroundColor: '#15354F' } : undefined}
            >
              {t('diagnose.backToCaseHome')}
            </button>
          </>
        )}

        {/* Not shared / Pending / Confirmed / Analyzing: just "Back to Case" */}
        {(state === 'not-shared' || state === 'pending' || state === 'confirmed' || state === 'checked_in' || state === 'external-analyzing' || state === 'external-pending') && (
          <>
            <button
              onClick={() => navigate('/case/home')}
              className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-1.5"
            >
              {t('diagnose.backToCaseHome')}
            </button>
            {/* v200.2: Cancel & withdraw — proper button below Back to Case Home.
                ★ v242.152.0 — asks the shared predicate instead of naming two
                  screen states, so this surface and Case Home cannot disagree
                  about when a case may be taken back. */}
            {canWithdrawCase(currentCase) && (
              <button
                onClick={() => setShowWithdrawModal(true)}
                className="w-full py-2.5 rounded-full border-[1.5px] border-red-200 text-red-500 text-[13px] font-semibold flex items-center justify-center gap-1.5 mt-2 active:scale-[0.98] transition-all"
                style={{ backgroundColor: '#fef2f2' }}
              >
                <X size={14} />
                {t('diagnose.cancelWithdrawCase')}
              </button>
            )}
          </>
        )}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes ringPulse { 0%,100% { transform:scale(1); opacity:0.4; } 50% { transform:scale(1.15); opacity:0; } }
        @keyframes blink { 0%,100% { opacity:0.3; } 50% { opacity:1; } }
      `}</style>

      {/* ★★ v242.207.0 — the share-code link's doctor panel. Same sheet chrome as the
          manage sheet below (portal to document.body, safe-area bottom padding).
          P11 step 4 (D1): the JSX lives in components/share/ShareDoctorPanel.jsx, the one
          owner ShareChoiceScreen's Orella card renders too. */}
      {showDoctorPanel && (
        <ShareDoctorPanel
          loading={doctorPanel.loading}
          yourDoctors={doctorPanel.yourDoctors}
          orellaDoctors={doctorPanel.orellaDoctors}
          insurance={checkInProfile?.insurance || {}}
          onPick={handlePanelDoctorPick}
          onClose={() => setShowDoctorPanel(false)}
          onMoreOptions={() => { setShowDoctorPanel(false); setShowManageSheet(true) }}
          closeLabel={t('diagnose.keepSharing')}
        />
      )}

      {/* v225.15.0 (C-A-174): unified "Change or cancel sharing" sheet for the share-code sequence */}
      {showManageSheet && (() => {
        const busy = isRevoking || isRegenerating || isWithdrawing
        const close = () => { if (!busy) setShowManageSheet(false) }
        // ★★★ v242.87.0 (TF b34 #15) — "Not 100% displayed": the "Withdraw the
        //   case" row and the "Keep sharing" button below it were off-screen.
        //   TWO independent defects, and fixing either alone leaves it broken:
        //     (i)  pb-6 is 24px. The home indicator alone is 34px, so the last
        //          row was under it even when the sheet was positioned right.
        //     (ii) `fixed inset-0` was NOT viewport-fixed. /case/diagnose is
        //          wrapped in PageWrapper, a framer-motion motion.div, and a
        //          transformed ancestor becomes the containing block for
        //          position:fixed — so inset-0 resolved to PageWrapper's box,
        //          which is as tall as the whole scrollable page. No z-index
        //          change can fix that; z-[60] over TabBar's z-50 was already
        //          correct.
        //   This is the same bug HomeScreen.jsx:706 documents as TF-39, with
        //   the same remedy: portal to document.body to escape the containing
        //   block, and pad the bottom with the real inset.
        //
        // ★★★ v242.89.0 (TF b37) — AND THAT REMEDY SHIPPED WITHOUT ITS SECOND
        //   ARGUMENT. `createPortal(children)` is not a portal to anywhere:
        //   React runs `isValidContainer(undefined)`, fails it, and throws a
        //   minified #200. So from v242.87.0 through Build 37, tapping "Change
        //   or cancel sharing" did not open a misplaced sheet — it took the
        //   whole screen down to the error boundary. The container below is
        //   the fix, and `scripts/check-portal-container.mjs` is the gate, an
        //   AST arm on call ARITY. It has to be an AST arm: the word
        //   `document.body` appears three lines above this call, so any
        //   substring arm scores GREEN on the crash. Measured, not assumed.
        return createPortal(
          <div className="fixed inset-0 z-[60] flex items-end" style={{ background: 'rgba(15,30,45,0.45)' }} onClick={close}>
            <div className="w-full bg-white rounded-t-[22px] px-4 pt-2" onClick={(e) => e.stopPropagation()} data-safe-bottom="1" style={{ animation: 'manageUp .25s ease', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))', maxHeight: '88dvh', overflowY: 'auto', overscrollBehavior: 'contain' }}>
              <style>{`@keyframes manageUp{from{transform:translateY(40px);opacity:.4}to{transform:translateY(0);opacity:1}}`}</style>
              <div className="w-10 h-1 rounded-full mx-auto my-2" style={{ background: '#D6DEE6' }} />
              <p className="text-[17px] font-bold px-0.5 mb-1" style={{ color: '#15354F' }}>{t('diagnose.manageTitle')}</p>
              <p className="text-[12.5px] px-0.5 mb-4 leading-relaxed" style={{ color: '#5A6B7B' }}>
                {shareNotice?.kind === 'failed'
                  ? t('shareCodeKill.mintFailed')
                  : displayShareCode
                  ? <>{t('diagnose.manageCasePrefix')} <span className="font-semibold" style={{ color: '#15354F' }}>{displayShareCode}</span> {t('diagnose.manageNotClaimedSuffix')}</>
                  : t('diagnose.manageWhatToDoShared')}
              </p>

              {/* Primary — switch to an Orella MD */}
              <button onClick={handleCancelToOrella} disabled={busy}
                className="flex items-center gap-3 w-full text-left rounded-[15px] p-3.5 mb-2.5 active:scale-[0.99] transition-transform disabled:opacity-60"
                style={{ border: '1.5px solid #46A8F9', background: '#F3F9FF' }}>
                <span className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#46A8F9' }}>
                  {isRevoking ? <OrellaSpinner size={20} tone="inherit" className="text-white" /> : <Stethoscope size={20} className="text-white" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-semibold leading-tight" style={{ color: '#15354F' }}>
                    {t('diagnose.sendToOrellaDoc')}
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: '#46A8F9', background: '#E2F0FE' }}>{t('diagnose.recommended')}</span>
                  </span>
                  <span className="block text-[11.5px] mt-1 leading-snug" style={{ color: '#5A6B7B' }}>{t('diagnose.pickBoardCertified')}</span>
                </span>
                <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#94A3B8' }} />
              </button>

              {/* Regenerate code.
                  ★★ PROD-941 (Alex 2026-10-12 Q20 (c)): hidden on a case a physician holds or was
                  sent. The API refuses both the renew and the mint there (generate_share_code,
                  PROD-509), so the row could only ever end in "could not be renewed right now".
                  Hidden, not disabled: no new copy, and the sheet keeps its other rows' spacing.
                  Owner: utils/shareCodeRow.js. Gate: scripts/check-share-code-row.mjs. */}
              {offersNewShareCode(currentCase) && (
              <button onClick={handleRegenerateCode} disabled={busy}
                className="flex items-center gap-3 w-full text-left rounded-[15px] p-3.5 mb-2.5 active:scale-[0.99] transition-transform disabled:opacity-60"
                style={{ border: '1.5px solid #E2E8F0', background: '#fff' }}>
                <span className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#E9F8FF' }}>
                  {isRegenerating ? <OrellaSpinner size={20} /> : <RefreshCw size={20} style={{ color: '#46A8F9' }} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-semibold leading-tight" style={{ color: '#15354F' }}>{t('diagnose.generateNewCode')}</span>
                  <span className="block text-[11.5px] mt-1 leading-snug" style={{ color: '#5A6B7B' }}>{t('diagnose.regenerateIfExpired')}</span>
                </span>
                <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#94A3B8' }} />
              </button>
              )}

              {/* Withdraw */}
              <button onClick={handleWithdrawCase} disabled={busy}
                className="flex items-center gap-3 w-full text-left rounded-[15px] p-3.5 active:scale-[0.99] transition-transform disabled:opacity-60"
                style={{ border: '1.5px solid #F3C9C9', background: '#FEF5F5' }}>
                <span className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FBE3E3' }}>
                  {isWithdrawing ? <OrellaSpinner size={20} tone="inherit" /> : <AlertTriangle size={20} style={{ color: '#C0392B' }} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-semibold leading-tight" style={{ color: '#A8322B' }}>{t('diagnose.withdrawTheCase')}</span>
                  <span className="block text-[11.5px] mt-1 leading-snug" style={{ color: '#5A6B7B' }}>{t('diagnose.saveDraftShareLater')}</span>
                </span>
                <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#94A3B8' }} />
              </button>

              <button onClick={close} disabled={busy} className="w-full text-center py-3 mt-1 text-[13.5px] font-semibold disabled:opacity-60" style={{ color: '#5A6B7B' }}>
                {t('diagnose.keepSharing')}
              </button>
            </div>
          </div>,
          document.body
        )
      })()}

      {/* ★★★ P11 (TEST 1.08) — the share-code kill confirmation. A sibling of the manage
          sheet, so it stacks above it (ConfirmModal is z-[9999], the sheet z-[60]). Every
          label is a shareCodeKill key: ConfirmModal's own defaults are English. */}
      <ConfirmModal
        open={!!shareKill}
        title={t('shareCodeKill.title')}
        message={`${shareKill === 'orella' ? t('shareCodeKill.orellaBody') : t('shareCodeKill.regenerateBody')} ${t('shareCodeKill.appointmentAlso')}`}
        confirmLabel={shareKill === 'orella' ? t('shareCodeKill.confirmOrella') : t('shareCodeKill.confirmRegenerate')}
        cancelLabel={t('shareCodeKill.keep')}
        loading={isRegenerating || isRevoking}
        icon="warning"
        onConfirm={() => shareKill === 'orella' ? handleCancelToOrella(true) : handleRegenerateCode(true)}
        onCancel={() => { if (!(isRegenerating || isRevoking)) setShareKill(null) }}
      />

      <CaseReportViewer open={showReportViewer} caseObj={currentCase} onClose={() => setShowReportViewer(false)} t={t} />
      <SwitchDoctorSheet
        open={showSwitchSheet}
        testId="diagnose-switch-sheet"
        title={t('diagnose.switchTitle', 'Share with another doctor?')}
        body1={t('diagnose.switchBody1', "First we'll end your current doctor's access to your case. Your share code stops working, and an office you faxed keeps the report it already received.")}
        body2={t('diagnose.switchBody2', "Then you'll choose your new doctor, on Orella or anywhere in the US, and we'll make a new share code for them.")}
        confirmLabel={t('shareHub.differentOfficeConfirm', 'End access and choose a new doctor')}
        keepLabel={t('diagnose.keepSharing')}
        busy={switching}
        onConfirm={async () => {
          if (switching) return
          setSwitching(true)
          try {
            await withdrawCaseFromDoctor(currentCase.id, updateAndSaveCase, currentCase)
            setShowSwitchSheet(false)
            navigate('/case/share-hub')
          } catch (e) {
            setSwitching(false)
          }
        }}
        onClose={() => setShowSwitchSheet(false)}
      />

      {/* v138.1.0: Withdraw case from doctor confirmation modal */}
      <ConfirmModal
        open={showWithdrawModal}
        title={t('diagnose.withdrawCase')}
        message={displayShareCode ? t('shareCodeKill.withdrawBody') : t('diagnose.cancelWarning')}
        confirmLabel={t('shareCodeKill.confirmWithdraw')}
        cancelLabel={displayShareCode ? t('shareCodeKill.keep') : t('diagnose.keepAppointmentLabel')}
        confirmColor="#EF4444"
        loading={isWithdrawing}
        icon="warning"
        onConfirm={() => handleWithdrawCase(true)}
        onCancel={() => { if (!isWithdrawing) setShowWithdrawModal(false) }}
      />

      <SubmitOverlay
        phase={submitPhase}
        processingTitle={t('diagnose.savingAcknowledgment')}
        processingSubtitle={t('diagnose.updatingYourCarePlan')}
        successTitle={t('diagnose.diagnoseComplete')}
        successSubtitle={t('diagnose.headingToYourNextSteps')}
      />

      {/* v175.6.0: Pre-visit check-in modal */}
      <CheckInModal
        isOpen={showCheckIn}
        onClose={() => setShowCheckIn(false)}
        doctor={currentCase?.doctor || currentCase?.sentToDoctorData}
        appointment={{
          // ★ PROD-1007: a held reschedule checks in to the old confirmed slot, never the requested one.
          date: checkInHeldSlot?.date || currentCase?.appointmentRequest?.confirmedDate || currentCase?.appointmentRequest?.requestedDate,
          time: checkInHeldSlot?.time || currentCase?.appointmentRequest?.confirmedTime || currentCase?.appointmentRequest?.requestedTime,
          type: checkInHeldSlot?.type || currentCase?.appointmentRequest?.confirmedType || currentCase?.appointmentRequest?.appointmentType,
          // v242.79.0 (TF-36) — the join link, threaded through for the wallet
          // pass. This object carried only date/time/type, so CheckInModal had
          // nothing to print for a virtual visit no matter what it asked for.
          // The link lives on the APPOINTMENT ROW, not on appointmentRequest
          // (DeliverScreen reads the same two fields off apiAppt), so take it
          // from the initial appointment for this case.
          // MEET PA2: handed as the join target (link, id, provider, decision) for the pass and the
          //   check-in Join button (JoinVideoVisit).
          join: videoJoinTarget(initialAppointmentRow(currentCase), currentCase?.appointmentRequest),
        }}
      />
    </div>
  )
}
